// Every user-facing string lives here so translation can be added later (spec 6.7).

import type {
  ChangeAction,
  ChangelogCategory,
  DeploymentStatus,
  DriftState,
  EventLevel,
  Permission,
  ResourceRunStatus,
  Role,
  RunKind,
  RunStatus,
} from './api/types';
import { networkDeployment } from './networks/strings/deployment';
import { networkEntryPoints } from './networks/strings/entryPoints';
import { networkLists } from './networks/strings/lists';
import { networkPage } from './networks/strings/page';
import { networkRequests } from './networks/strings/requests';
import { networkRuns } from './networks/strings/runs';
import { networkShared } from './networks/strings/shared';
import { networkTopology } from './networks/strings/topology';

export const strings = {
  product: 'Nebisys',
  // Feature spec 17 (networking): one file per part of the UI under `src/networks/strings/`.
  networks: {
    ...networkShared,
    page: networkPage,
    lists: networkLists,
    deployment: networkDeployment,
    runs: networkRuns,
    topology: networkTopology,
    requests: networkRequests,
    entryPoints: networkEntryPoints,
  },
  common: {
    loading: 'Loading…',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    remove: 'Remove',
    add: 'Add',
    retry: 'Retry',
    next: 'Next page',
    previous: 'Previous page',
    none: 'None',
    notSet: 'Not set',
    yes: 'Yes',
    no: 'No',
    copy: 'Copy',
    copied: 'Copied',
    secret: 'secret',
    required: 'required',
    optional: 'optional',
    empty: 'Nothing here yet.',
    dash: '–',
    confirm: 'Confirm',
    back: 'Back',
    unknownError: 'Something went wrong.',
    requiresRole: (role: Role) => `Requires the ${role} role.`,
    forbidden: 'You don’t have permission to do this.',
    notFound: 'Not found.',
    networkError: 'The server could not be reached.',
    by: (user: string) => `by ${user}`,
    any: 'Any',
  },
  relative: {
    justNow: 'just now',
    minutes: (n: number) => `${n} min ago`,
    hours: (n: number) => `${n} h ago`,
    yesterday: 'yesterday',
    days: (n: number) => `${n} days ago`,
  },
  nav: {
    main: 'Main navigation',
    blueprints: 'Blueprints',
    cloudAccounts: 'Cloud accounts',
    about: 'About',
    signOut: 'Sign out',
    signedInAs: (name: string) => `Signed in as ${name}`,
    roles: (roles: string) => `Roles: ${roles}`,
  },
  auth: {
    signingIn: 'Signing in…',
    signInFailed: 'Sign-in failed.',
    signInAgain: 'Sign in again',
    configFailed: 'The Nebisys configuration could not be loaded.',
  },
  roles: {
    reader: 'reader',
    designer: 'designer',
    operator: 'operator',
    admin: 'admin',
    'network-admin': 'network-admin',
    'secret-reader': 'secret-reader',
  } satisfies Record<Role, string>,
  // The application shell (spec 6.1).
  shell: {
    skipToMain: 'Skip to main content',
    home: 'Nebisys home',
    breadcrumb: 'Breadcrumb',
    kind: { blueprint: 'Blueprint', deployment: 'Deployment', run: 'Run' },
    switcher: (kind: string, name: string | null) => (name ? `${kind} ${name}, switch` : `${kind}, switch`),
    pages: {
      blueprints: 'Blueprints',
      designer: 'Designer',
      versions: 'Versions',
      deployments: 'Deployments',
      allDeployments: 'Deployments',
      runs: 'Runs',
      cloudAccounts: 'Cloud accounts',
      help: 'Help',
      about: 'About',
      whatsNew: 'What’s new',
    },
    allBlueprints: 'All blueprints',
    allDeployments: 'All deployments',
    allRuns: 'All runs',
    noDeployments: 'No deployments yet',
    noRuns: 'No runs yet',
    loadingMenu: 'Loading…',
    loadFailed: 'Could not be loaded',
    statusDot: (status: string) => `Status: ${status}`,
    runLabel: (kind: string, time: string) => `${kind} · ${time}`,
    search: 'Search or run a command…',
    shortcutsButton: 'Keyboard shortcuts',
    theme: {
      button: (choice: string) => `Theme: ${choice}`,
      menu: 'Theme',
      system: 'System',
      light: 'Light',
      dark: 'Dark',
    },
    user: {
      button: (name: string) => `User menu for ${name}`,
      roles: (roles: string) => `Roles: ${roles}`,
      noRoles: 'No roles',
      canHeading: 'You can',
      permissions: {
        read: 'See blueprints, deployments and runs',
        design: 'Edit blueprints and their secrets',
        operate: 'Plan, apply and destroy deployments; set secret values; see cloud accounts',
        admin: 'Manage cloud accounts',
        'manage-networks': 'Manage networks, their address plans and runs',
        'reveal-secrets': 'Reveal secret values',
      } satisfies Record<Permission, string>,
      signOut: 'Sign out',
    },
    activeRuns: {
      button: (n: number) => (n === 1 ? '1 run active' : `${n} runs active`),
      menu: 'Active runs',
      all: 'All active runs',
      queued: 'Queued',
      item: (kind: string, deployment: string, blueprint: string) => `${kind} · ${deployment} · ${blueprint}`,
      progress: (completed: number, total: number) => `${completed}/${total}`,
    },
    phone: {
      back: 'Back',
      more: 'More',
      moreMenu: 'More destinations',
      pageMenu: 'Page menu',
      search: 'Search or run a command',
      launchers: 'Tool windows',
      expand: (title: string) => `Expand ${title}`,
      collapse: (title: string) => `Collapse ${title}`,
      close: (title: string) => `Close ${title}`,
      dragHandle: 'Drag to resize',
    },
    rail: {
      label: 'Main navigation',
      blueprints: 'Blueprints',
      deployments: 'Deployments',
      runs: 'Runs',
      cloudAccounts: 'Cloud accounts',
      help: 'Help',
      about: 'About Nebisys',
      whatsNew: 'What’s new',
      shortcuts: 'Keyboard shortcuts',
      version: (version: string) => `Nebisys ${version}`,
    },
    status: {
      label: 'Status bar',
      online: 'Live updates on',
      reconnecting: 'Reconnecting…',
      offline: 'Offline · retrying',
      designer: (blueprint: string, version: number, unsaved: boolean) =>
        `${blueprint} · v${version}${unsaved ? ' · unsaved changes' : ''}`,
      deployment: (blueprint: string, deployment: string) => `${blueprint} · ${deployment}`,
      run: (deployment: string, kind: string) => `${deployment} · ${kind} run`,
    },
    palette: {
      title: 'Command palette',
      input: 'Search or run a command',
      noResults: 'No results',
      groups: { goTo: 'Go to', blueprints: 'Blueprints', deployments: 'Deployments', commands: 'Commands' },
      goBlueprints: 'Blueprints',
      goDeployments: 'Deployments',
      goRuns: 'Runs',
      goCloudAccounts: 'Cloud accounts',
      goAbout: 'About Nebisys',
      goWhatsNew: 'What’s new',
      themeSystem: 'Theme: System',
      themeLight: 'Theme: Light',
      themeDark: 'Theme: Dark',
      toggleTheme: 'Toggle theme',
      shortcuts: 'Keyboard shortcuts',
      nextRegion: 'Focus next region',
      signOut: 'Sign out',
    },
    shortcuts: {
      title: 'Keyboard shortcuts',
      everywhere: 'Everywhere',
      designer: 'Designer',
      palette: 'Open the command palette',
      help: 'Show keyboard shortcuts',
      goBlueprints: 'Go to Blueprints',
      goDeployments: 'Go to Deployments',
      goRuns: 'Go to Runs',
      goCloudAccounts: 'Go to Cloud accounts',
      focusSearch: 'Focus the search of the list',
      nextRegion: 'Focus the next region',
      previousRegion: 'Focus the previous region',
      close: 'Close a menu, dialog or the palette',
      save: 'Save the blueprint',
      undo: 'Undo',
      redo: 'Redo',
      nodeFocus: 'Move between nodes on the canvas',
      nodeMove: 'Move the selected node by one grid step',
      nodeDelete: 'Delete the selection',
      nodeInspect: 'Open the inspector for the selected node',
      arrowKeys: 'Arrow keys',
      then: 'then',
      or: 'or',
    },
    initials: (name: string) => {
      const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
      const letters = words.length >= 2 ? words[0][0] + words[1][0] : (words[0] ?? name).slice(0, 2) || '?';
      return letters.toUpperCase();
    },
  },
  blueprints: {
    title: 'Blueprints',
    subtitle: 'Cloud-neutral designs. Each can be deployed to many environments.',
    newBlueprint: 'New blueprint',
    moreCreate: 'More ways to create a blueprint',
    importJson: 'Import JSON…',
    importJsonButton: 'Import JSON',
    name: 'Name',
    description: 'Description',
    latestVersion: 'Latest version',
    version: 'Version',
    updated: 'Updated',
    actions: 'Actions',
    empty: 'No blueprints yet.',
    createTitle: 'New blueprint',
    nameHelp: '1–100 characters.',
    nameRequired: 'Enter a name.',
    nameTooLong: 'The name can be at most 100 characters.',
    open: (name: string) => `Open ${name}`,
    openButton: 'Open',
    versions: 'Versions',
    deployments: 'Deployments',
    designer: 'Designer',
    filter: 'Filter by name or description',
    deploymentsFilter: 'Deployments',
    deploymentsFilterValues: {
      any: 'any',
      deployed: 'deployed',
      notDeployed: 'not deployed',
      problems: 'with problems',
    },
    updatedByFilter: 'Updated by',
    anyone: 'anyone',
    order: 'Most recently updated first',
    filteringNote: 'Filtering the loaded blueprints.',
    noMatch: 'No loaded blueprint matches the filters.',
    notDeployed: 'Not deployed',
    moreDeployments: (n: number, capped: boolean) => (capped ? `+${n} or more` : `+${n}`),
    moreDeploymentsLabel: (n: number, capped: boolean) =>
      capped ? `${n} or more other deployments` : `${n} other ${n === 1 ? 'deployment' : 'deployments'}`,
    behind: (n: number) => `${n} behind`,
    behindTitle: 'Deployments run older versions of this blueprint.',
    chip: {
      running: 'running',
      failed: 'failed',
      drift: 'drift',
      new: 'new',
      destroyed: 'destroyed',
      runKind: {
        plan: 'planning',
        apply: 'applying',
        destroy: 'destroying',
        refresh: 'refreshing',
      } satisfies Record<RunKind, string>,
    },
    deploymentChipsLabel: (name: string) => `Deployments of ${name}`,
    moreActions: (name: string) => `More actions for ${name}`,
    duplicate: 'Duplicate',
    exportJson: 'Export JSON',
    delete: 'Delete',
    copyName: (name: string) => `${name} copy`,
    deleteTitle: 'Delete blueprint',
    deleteConfirm: (name: string) => `Delete ${name}? Its versions are deleted too.`,
    deleteBlocked: 'Delete or destroy these deployments first.',
    emptyTitle: 'Design your first blueprint',
    emptyText:
      'A blueprint describes web apps, functions, databases, buckets and queues and how they connect, once, for any cloud. You deploy it later to Azure, AWS or Google Cloud.',
    emptyReader: 'Ask someone with the designer role to create one.',
    startFrom: 'Start from',
    startEmpty: 'Empty',
    startImport: 'Import file',
    file: 'Blueprint file (.json)',
    invalidJson: 'The file isn’t valid JSON.',
    notBlueprint: 'The file isn’t a Nebisys blueprint document.',
    descriptionHelp: 'Optional.',
  },
  designer: {
    palette: 'Resource types',
    paletteHelp: 'Drag a type onto the canvas, or press Enter to add it.',
    canvas: 'Blueprint canvas',
    inspector: 'Inspector',
    resizeInspector: 'Resize inspector',
    problems: 'Problems',
    problemsCount: (errors: number, warnings: number) => `${errors} errors, ${warnings} warnings`,
    noProblems: 'No problems.',
    showProblems: 'Show problems',
    hideProblems: 'Hide problems',
    save: 'Save',
    saved: 'Saved',
    unsaved: 'Unsaved changes',
    undo: 'Undo',
    redo: 'Redo',
    fitView: 'Fit view',
    readOnly: 'Read-only',
    readOnlyVersion: (version: number) => `Version ${version} (read-only)`,
    backToLatest: 'Open latest version',
    leaveTitle: 'Leave the designer?',
    leaveMessage: 'You have unsaved changes. They will be lost.',
    leave: 'Leave',
    stay: 'Stay',
    validating: 'Validating…',
    layoutSaveFailed: 'The canvas layout could not be saved.',
    deploymentOverlay: 'Deployment overlay',
    noDeployment: 'No deployment',
    liveRun: 'Live run',
    errorsBadge: (n: number) => `${n} ${n === 1 ? 'error' : 'errors'}`,
    warningsBadge: (n: number) => `${n} ${n === 1 ? 'warning' : 'warnings'}`,
    connectMenu: 'Connection type',
    connectionExists: 'This connection already exists.',
    nodeLabel: (id: string, type: string) => `${id}, ${type}`,
    connectHandle: 'Drag to connect',
    edgeLabel: (source: string, target: string, type: string) => `${type} from ${source} to ${target}`,
    waitingForCode: 'Waiting for code',
    waitingForCodeHelp: 'Deploy the function code with your pipeline, then apply again.',
    vaultNode: 'Vault',
    vaultNodeLabel: (deployment: string) => `Vault of deployment ${deployment}`,
    highlighted: 'Uses the selected secret',
    versionSaved: (v: number) => `v${v} · saved`,
    versionUnsaved: (v: number) => `v${v} · unsaved changes`,
    readOnlyV: (v: number) => `Read-only v${v}`,
    openLatest: 'Open latest',
    overlay: 'Overlay',
    designOnly: 'Design only',
    versions: 'Versions',
    deploy: 'Deploy',
    planOn: (name: string) => `Plan on ${name}`,
    deployMenu: 'Deploy options',
    newDeployment: 'New deployment…',
    saveFirst: 'Save your changes first.',
    noDeployments: 'No deployments yet',
    filterTypes: 'Filter types',
    filterTypesLabel: 'Filter resource types',
    noTypeMatches: 'No type matches.',
    collapsePalette: 'Collapse palette',
    expandPalette: 'Expand palette',
    notOn: (provider: string) => `Not on ${provider}`,
    notOnReason: (type: string, provider: string) =>
      `${type} isn’t available on ${provider}, the deployment’s provider.`,
    addResource: 'Add a resource',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomReset: (percent: number) => `Zoom ${percent} %, reset to 100 %`,
    showMinimap: 'Show minimap',
    zoomControls: 'Canvas zoom',
    countsLabel: (errors: number, warnings: number) =>
      [
        errors ? `${errors} ${errors === 1 ? 'error' : 'errors'}` : '',
        warnings ? `${warnings} ${warnings === 1 ? 'warning' : 'warnings'}` : '',
      ]
        .filter(Boolean)
        .join(', '),
    overlayStatus: (status: string, region: string | undefined) =>
      region ? `${status} in ${region}` : status,
    selectConnection: (label: string) => `Select connection ${label}`,
    inspectorOpen: 'Inspector',
    closeInspector: 'Close the inspector',
  },
  inspector: {
    sections: 'Inspector sections',
    tab: {
      general: 'General',
      properties: 'Properties',
      triggers: 'Triggers',
      deploy: 'Deploy',
      overrides: 'Overrides',
    } as Record<string, string>,
    tabCount: (label: string, count: string) => `${label}, ${count}`,
    moreSections: 'More sections',
    overridesProvider: 'Provider',
    more: (id: string) => `More actions for ${id}`,
    duplicate: 'Duplicate',
    deleteShort: 'Delete',
    copyId: 'Copy ID',
    copied: 'ID copied',
    openVariables: 'Open in Variables window',
    connections: 'Connections',
    noConnections: 'No connections yet.',
    outgoing: (type: string, target: string) => `${type} → ${target}`,
    incoming: (type: string, source: string) => `${source} → ${type}`,
    readOnlyRole: 'Read-only: you need the designer role to edit.',
    readOnlyVersion: (v: number) => `You’re viewing version ${v}.`,
    resource: 'Resource',
    connection: 'Connection',
    blueprint: 'Blueprint',
    id: 'ID',
    idHelp: 'Lowercase letters, digits and hyphens; starts with a letter; at most 30 characters.',
    idInvalid: 'Use lowercase letters, digits and hyphens, starting with a letter (at most 30).',
    idTaken: 'Another resource already has this ID.',
    renameWarning: (oldId: string) =>
      `Renamed. Existing deployments with placement overrides for “${oldId}” need updating.`,
    name: 'Name',
    nameHelp: 'Used in cloud resource names. Defaults to the ID.',
    description: 'Description',
    type: 'Type',
    placement: 'Placement',
    provider: 'Provider',
    region: 'Region',
    anyProvider: 'From the deployment',
    anyRegion: 'From the deployment',
    regionExpression: 'Region from a parameter',
    properties: 'Properties',
    overrides: (provider: string) => `${provider} overrides`,
    overridesHelp: 'Native settings used only when this resource runs on this provider.',
    connectTo: 'Connect to…',
    connectTarget: 'Target resource',
    connectType: 'Connection type',
    connect: 'Connect',
    noTargets: 'There are no other resources to connect to.',
    deleteResource: 'Delete resource',
    deleteConnection: 'Delete connection',
    alias: 'Alias',
    aliasHelp: 'Prefix for the environment variables. Uppercase letters, digits and underscores.',
    triggerAlias: 'Connection name (alias)',
    triggerAliasHelp:
      'The connection name the function’s code uses for the trigger binding. Letters, digits and underscores; starts with a letter.',
    triggerAliasInvalid: 'Use letters, digits and underscores, starting with a letter (at most 64).',
    triggerAliasTaken: 'Another trigger of this function app uses this connection name.',
    aliasInvalid: 'Use uppercase letters, digits and underscores, starting with a letter (at most 32).',
    functionName: 'Function name',
    functionNameHelp:
      'The function in the code that handles the events. This source pushes events to one function.',
    functionNameOptionalHelp: (source: string) =>
      `Optional. When set, Nebisys checks that the function exists before it connects ${source}.`,
    functionNameInvalid: 'Use letters, digits and underscores, not starting with a digit (at most 128).',
    functionNameRequired: 'This source needs the name of the function that handles its events.',
    triggerSettings: (alias: string) =>
      `The function receives settings whose names start with ${alias}; the provider defines the rest of the name.`,
    environment: 'Environment variables the source receives',
    noEnvironment: 'This connection type passes no values.',
    settingsTab: 'Settings',
    source: 'Source',
    target: 'Target',
    blueprintName: 'Name',
    blueprintDescription: 'Description',
    parameters: 'Parameters',
    multipleSelected: (n: number) => `${n} elements selected.`,
    deleteSelection: 'Delete selection',
  },
  triggerDialog: {
    title: (target: string, source: string) => `Trigger ${target} from ${source}`,
    intro: (source: string, target: string) => `Events from ${source} start the function app ${target}.`,
    create: 'Add trigger',
    receives: (target: string) => `${target} receives`,
    forThisFunction: 'for this function',
    waitsForCode: (source: string) =>
      `Nebisys connects ${source} to the function on the first apply after your pipeline deploys the code.`,
  },
  /** The connect menu and Connect to… (feature spec 16, 9.2): no type names, only rules and IDs. */
  connect: {
    header: (source: string, target: string) => `Connect ${source} → ${target}`,
    and: 'and',
    connectsTo: (source: string, target: string, keys: string, access: string) =>
      `${source} uses ${target}: gets ${keys}${access}.`,
    access: {
      identity: ', and its identity gets access',
      network: ', and reaches it over its network',
      none: '',
    } as Record<string, string>,
    accessRoles: (roles: string) => `Access: ${roles}.`,
    triggers: (source: string, target: string) => `${source}’s events start ${target}.`,
    dependsOn: (source: string, target: string) =>
      `${target} is created after ${source}. No settings, no access.`,
    otherDirection: 'Other direction',
    reverseLabel: (source: string, target: string) => `${source} triggers ${target}`,
    reverseDescription: (source: string, target: string) =>
      `${source}’s events start ${target}. Reverses the arrow and asks for the connection name.`,
    notAvailable: 'not available',
    requirement: (source: string, property: string, value: string, values: string) =>
      `${source}: ${property} is ${value}; this needs ${values}.`,
    requirementHelpTriggers: (source: string, target: string, values: string) =>
      `${source} triggers ${target}; only ${values} can trigger a function.`,
    requirementHelpConnects: (source: string, target: string, values: string) =>
      `${source} connects to ${target}; only ${values} can connect.`,
    unavailableLine: (type: string, message: string, detail: string) =>
      `${type} isn’t available: ${message} ${detail}`,
    triggerInstead: (self: string, target: string) => `Trigger ${self} from ${target} instead`,
    cancelHint: 'Esc',
  },
  /** Provider descriptions (feature spec 16, 9.3): provenance and the generic sentences. */
  descriptions: {
    serviceLine: (provider: string, names: string) => `${provider}: ${names}`,
    serviceSubtitle: (type: string, service: string) => `${type} · ${service}`,
    fromOverlay: (provider: string, deployment: string, resource: string) =>
      `From the ${provider} connector: the overlay ${deployment} places ${resource} on ${provider}.`,
    fromPlacement: (provider: string, resource: string) =>
      `From the ${provider} connector: ${resource} is placed on ${provider}.`,
    fromOnly: (provider: string, type: string) =>
      `From the ${provider} connector: the only provider that supports ${type}.`,
    fromAgreeing: (providers: string, type: string) =>
      `From the ${providers} connectors: every provider that supports ${type} describes it the same way.`,
    roles: (roles: string) => `Access: ${roles}.`,
    noRole: (source: string) => `No role: ${source} pushes events to the function.`,
    changesApp: 'changes the function app',
    noRoleShort: 'no role',
    loadFailed: 'Couldn’t load the provider’s trigger details.',
    retry: 'Retry',
    loading: 'Loading the provider’s details…',
    youNeedTo: 'You need to:',
    copy: 'Copy',
    copySnippet: 'Copy the snippet',
    connectsToAccess: (roles: string, source: string) => `Access: ${roles} for ${source}’s identity.`,
    connectsToIdentity: (source: string, target: string) =>
      `No key or password: ${source} signs in with its own identity, which Nebisys grants access to ${target}.`,
  },
  declarations: {
    title: 'Secrets',
    help: 'Secrets the settings use as ${secret.<name>}. Values never appear in the blueprint; they are set per deployment.',
    none: 'No secrets declared.',
    add: 'Add secret',
    name: 'Name',
    description: 'Description',
    onChange: 'When the value changes',
    wait: 'Wait: apps pick it up by themselves (within 24 hours)',
    restart: 'Restart the apps that use it',
    generate: 'Generate the value',
    generateHelp: 'Nebisys generates the value once (letters, digits, - and _) when the vault exists.',
    length: 'Length',
    lengthHelp: '16 to 128 characters.',
    remove: (name: string) => `Remove secret ${name}`,
    inspectorHint: 'Secret declarations are edited in the Variables window’s Secrets view.',
    inspectorButton: 'Edit secret declarations',
    tableLabel: 'Secret declarations',
    usedBy: 'Used by',
    edit: 'Edit declarations',
    back: (deployment: string) => `Back to ${deployment}’s secrets`,
    removed: (name: string) => `${name} removed. Undo with Ctrl+Z.`,
    usage: (n: number) =>
      n === 0 ? 'Not used by any setting.' : `Used by ${n} ${n === 1 ? 'setting' : 'settings'}.`,
    nameProblem: {
      invalid: 'Letters, digits and hyphens; starts with a letter or digit; at most 63 characters.',
      reserved: 'Names starting with “nebisys-” are reserved for system secrets.',
      taken: 'Another secret has this name (names are compared ignoring case).',
    },
  },
  settingsTab: {
    help: 'Settings become environment variables of the app. Plain values stay in Nebisys; secrets are references to the deployment’s vault.',
    name: 'Setting',
    source: 'Source',
    value: 'Value',
    status: 'Reference status',
    lastChange: 'Last change',
    actions: 'Actions',
    none: 'No settings.',
    add: 'Add setting',
    addTitle: 'New setting',
    nameHelp: 'Letters, digits and underscores; starts with a letter or underscore.',
    nameInvalid: 'Use letters, digits and underscores, not starting with a digit (at most 128).',
    nameTaken: 'This setting exists already, or a connection provides it.',
    kind: 'Kind',
    plainValue: 'Plain value',
    secretValue: 'Secret',
    existingSecret: 'Secret',
    newSecret: 'New secret…',
    newSecretName: 'New secret name',
    chooseSecret: 'Choose a secret',
    remove: (name: string) => `Remove setting ${name}`,
    valueOf: (name: string) => `Value of ${name}`,
    secretOf: (name: string) => `Secret of ${name}`,
    fromConnection: (resource: string) => `from ${resource}`,
    triggerPlaceholder: (resource: string) => `trigger values from ${resource} (named by the provider)`,
    hiddenSecret: 'secret, not shown',
    deploymentHint: (deployment: string) => `Live state in deployment ${deployment}.`,
    selectDeployment: 'Select a deployment above the canvas to see reference status and drift.',
    setValue: 'Set value',
    openInSecrets: 'Open in secrets view',
    notDeployed: 'Not deployed yet',
    referenceStatusError: (message: string) => `The reference status couldn’t be read: ${message}`,
    sources: {
      plain: 'plain',
      secret: 'secret',
      'system-secret': 'system secret',
      connection: 'connection',
      trigger: 'trigger',
    },
  },
  triggers: {
    title: 'Triggers',
    help: 'Resources whose events start this function. HTTP, timer, MCP and Durable Functions triggers live in code only.',
    count: (n: number) => `${n} ${n === 1 ? 'trigger' : 'triggers'}`,
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    header: (source: string, type: string, status: string | null) =>
      status ? `${source}, ${type}, ${status}` : `${source}, ${type}`,
    summary: (alias: string, settings: number, access: string) =>
      `${alias} · ${settings} ${settings === 1 ? 'setting' : 'settings'} · ${access}`,
    summaryGeneric: (alias: string) => `${alias} · names start with ${alias}`,
    yourStep: 'Your step',
    readyHelp: 'Nebisys has done its part.',
    showInVariables: (n: number) => `Show ${n} ${n === 1 ? 'setting' : 'settings'} in the Variables window`,
    copySetting: (name: string) => `Copy ${name}`,
    example: 'Binding example',
    copyExample: 'Copy the binding example',
    note: 'Note',
    selectConnection: 'Select connection',
    none: 'No resource triggers this function.',
    source: 'Source',
    alias: 'Connection name',
    functionName: 'Function',
    settings: 'Settings',
    role: 'Access',
    status: 'Status',
    roleIdentity: (source: string) => `The function’s identity can receive from ${source}.`,
    roles: (roles: string) => `Roles on the source: ${roles}`,
    waiting: 'Waiting for code',
    ready: 'Set up',
    notApplied: 'Not applied yet',
    waitingBanner: (n: number) =>
      `${n} trigger ${n === 1 ? 'connection waits' : 'connections wait'} for the function code. Deploy the code with your pipeline, then apply again.`,
  },
  /** External triggers (feature spec 16 9.6): the section, cards, dialog, remove popover and the kinds registry. */
  externalTriggers: {
    title: 'External triggers',
    help: "Triggers on systems Nebisys doesn't create, such as Kafka. Nebisys delivers their settings and creates nothing else.",
    none: 'No external triggers.',
    add: 'Add external trigger',
    edit: 'Edit',
    remove: 'Remove',
    editLabel: (name: string) => `Edit the external trigger ${name}`,
    removeLabel: (name: string) => `Remove the external trigger ${name}`,
    needsDesigner: 'Needs the designer role.',
    header: (name: string, kind: string) => `${name}, ${kind}`,
    summary: (kind: string, n: number) => `${kind} · ${n} ${n === 1 ? 'setting' : 'settings'}`,
    settings: 'Settings',
    noSettings: 'No settings listed.',
    missing: 'Missing',
    addSetting: 'Add setting',
    addSettingLabel: (name: string) => `Add the setting ${name}`,
    notApplied: 'Not applied',
    example: 'Binding example',
    copyExample: 'Copy the binding example',
    // Kinds registry (lib/externalTriggers.ts).
    kinds: {
      kafka: 'Apache Kafka',
      rabbitmq: 'RabbitMQ',
      connector: 'Managed connector',
      other: 'Other',
    } as Record<string, string>,
    suggestion: {
      brokerList: 'Broker list; differs per environment.',
      topic: 'Topic the trigger reads.',
      consumerGroup: 'Consumer group of the function.',
      username: 'User name, for example the API key.',
      password: 'Password, for example the API secret.',
      rabbitConnection: 'Connection string: amqps://user:password@host:5671/vhost',
      rabbitQueue: 'Queue the trigger reads.',
      runtimeUrl: "The connection's runtime URL from the connector namespace.",
    },
    // Dialog.
    dialogAdd: (app: string) => `Add an external trigger to ${app}`,
    dialogEdit: (name: string) => `Edit the external trigger ${name}`,
    name: 'Name',
    kind: 'Kind',
    description: 'Description',
    settingsItReads: 'Settings it reads',
    setting: 'Setting',
    source: 'Source',
    value: 'Value',
    suggested: (kind: string, name: string) => `Suggested for ${kind} · names start with ${name}`,
    otherSettings: (app: string) => `Other settings of ${app}`,
    listedSettings: 'Settings it reads',
    sourcePlain: 'Plain value',
    sourceParameter: 'Parameter',
    sourceSecret: 'Secret',
    newParameter: (name: string) => `New parameter ${name}`,
    newSecret: (name: string) => `New declaration ${name}`,
    newParameterHint: 'new parameter · set per deployment',
    existingParameterHint: 'existing parameter',
    newSecretHint: 'new secret · value set per deployment',
    existingSecretHint: 'existing declaration',
    existingSetting: 'existing setting',
    include: (name: string) => `Include ${name}`,
    sourceOf: (name: string) => `Source of ${name}`,
    valueOf: (name: string) => `Value of ${name}`,
    parameterOf: (name: string) => `Parameter for ${name}`,
    secretOf: (name: string) => `Secret declaration for ${name}`,
    editKeepsNames: 'Settings keep their names; rename them in the Variables window.',
    nameRequired: 'Enter a name.',
    namePattern: 'Use a letter first, then letters, digits or _ (at most 64).',
    nameTaken: (name: string) => `${name} is already an external trigger of this function.`,
    nameIsTrigger: (name: string, source: string) => `${name} is already the trigger from ${source}.`,
    descriptionTooLong: 'Use at most 500 characters.',
    valueRequired: (name: string) => `${name} needs a value, or uncheck it.`,
    problems: (n: number) => `${n} ${n === 1 ? 'problem' : 'problems'}:`,
    footer: (settings: number, parameters: number, secrets: number) =>
      `Adds ${settings} ${settings === 1 ? 'setting' : 'settings'}, ${parameters} ${parameters === 1 ? 'parameter' : 'parameters'} and ${secrets} secret ${secrets === 1 ? 'declaration' : 'declarations'} in one undo step.`,
    cancel: 'Cancel',
    submitAdd: 'Add external trigger',
    submitSave: 'Save',
    discard: 'Discard this external trigger?',
    discardYes: 'Discard',
    keepEditing: 'Keep editing',
    added: (name: string) => `External trigger ${name} added`,
    // Remove popover.
    removeTitle: (name: string) => `Remove the external trigger ${name}?`,
    alsoRemove: (n: number) => `Also remove its ${n} ${n === 1 ? 'setting' : 'settings'}`,
    kept: (names: string, other: string) => `Kept: ${names} (also read by ${other}).`,
    secretStays: (name: string) => `The secret declaration ${name} stays (Secrets view).`,
    undoHint: 'Undo with Ctrl+Z',
    removed: (name: string) => `External trigger ${name} removed`,
    // Variables window chip.
    chipName: (variable: string, name: string, kind: string) =>
      `${variable}, read by the external trigger ${name}, ${kind}`,
    chipTooltip: (kind: string) => `External trigger · ${kind}`,
    readBy: (name: string, kind: string) => `Read by the external trigger ${name} (${kind})`,
  },
  deployBox: {
    title: 'Deploy the code',
    help: 'Your pipeline deploys the code. Nebisys creates the infrastructure.',
    name: 'Function app',
    resourceGroup: 'Resource group',
    subscription: 'Subscription',
    commands: 'Example commands',
    roles: 'Roles the pipeline’s identity needs',
    roleFunction: 'Website Contributor on the function app',
    roleContainer: 'Storage Blob Data Contributor on the deployment container',
    notDeployed: 'Values appear after the first apply.',
    selectDeployment: 'Select a deployment to see its values.',
    copyCommand: 'Copy command',
  },
  vault: {
    title: 'Vault',
    status: 'Status',
    notCreated: 'Not created',
    ready: 'Ready',
    name: 'Name',
    region: 'Region',
    provider: 'Provider',
    prepare: 'Prepare vault',
    prepareHelp:
      'Creates only the vault, so you can set the secret values before the first apply. Generated secrets get their values right after.',
    managed: (provider: string) =>
      `Secrets are kept in a vault that Nebisys creates on ${provider}. Apps get references, never values.`,
    storeFallback: (store: string) =>
      `This provider has no managed vault: secret values come from the secret store “${store}” and are injected as plain settings.`,
    noSecrets: 'The blueprint declares no secrets.',
    openFull: 'Open the secrets view',
  },
  secrets: {
    tab: 'Secrets',
    overviewTab: 'Overview',
    tabs: 'Deployment views',
    name: 'Name',
    kind: 'Kind',
    hasValue: 'Value',
    lastChanged: 'Last changed',
    expires: 'Expires',
    usedBy: 'Used by',
    tags: 'Tags',
    drift: 'Drift',
    actions: 'Actions',
    none: 'No secrets.',
    noMatch: 'No secret matches the filters.',
    set: 'set',
    notSet: 'not set',
    filters: 'Filters',
    filterKind: 'Kind',
    filterDrift: 'Drift',
    filterResource: 'Used by resource',
    all: 'All',
    anyDrift: 'Any drift',
    noDrift: 'No drift',
    banner: {
      missing: (n: number) => `${n} missing ${n === 1 ? 'value' : 'values'}`,
      outside: (n: number) => `${n} ${n === 1 ? 'change' : 'changes'} outside Nebisys`,
      unmanaged: (n: number) => `${n} unmanaged ${n === 1 ? 'secret' : 'secrets'}`,
    },
    setValue: 'Set value',
    setAgain: 'Set a value again',
    accept: 'Accept',
    acceptHelp: 'Record the current version as known.',
    rewriteTags: 'Rewrite tags',
    ignore: 'Ignore',
    addDeclaration: 'Add declaration',
    declarationAdded: (name: string) =>
      `The secret “${name}” was declared in the blueprint. Save to keep it.`,
    showUsage: (name: string) => `Show where ${name} is used`,
    selectResource: (id: string) => `Select ${id}`,
    noTags: 'No tags',
    vaultFirst: 'Prepare the vault first.',
    readOnlySystem: 'System secrets are read-only.',
    accepted: 'The current version was accepted.',
    ignored: (name: string) => `“${name}” is ignored from now on.`,
    tagsRewritten: 'Tags were rewritten.',
    generated: 'generated',
    ignoredBadge: 'ignored',
    kinds: { declared: 'declared', system: 'system', unmanaged: 'unmanaged' },
    driftKinds: {
      'changed-outside': 'changed outside',
      'deleted-outside': 'deleted outside',
      disabled: 'disabled',
      expired: 'expired',
      'tags-edited': 'tags edited',
      unmanaged: 'unmanaged',
      'missing-value': 'missing value',
      rotated: 'rotated',
      'rotation-conflict': 'rotation conflicts',
    },
  },
  setSecret: {
    title: (name: string) => `Set the value of ${name}`,
    value: 'New value',
    valueHelp: 'Written straight to the vault as a new version. Nebisys never stores or shows it.',
    restart: 'Restart the apps that use it now',
    restartHelp: (apps: string) => `Used by: ${apps}.`,
    notUsed: 'No app uses this secret yet.',
    policy: (policy: string) => `The secret’s policy is “${policy}”.`,
    tooLarge: 'The value is larger than 25 KB.',
    required: 'Enter a value.',
    submit: 'Set value',
    saved: (name: string) => `A new value was set for “${name}”.`,
    restarted: (apps: string) => `Restarted: ${apps}.`,
    restartFailed: (n: number) => `${n} ${n === 1 ? 'app' : 'apps'} could not be restarted`,
    restartFailedDetail: (failures: string) =>
      `${failures}. The apps pick up the new value within 24 hours, or restart them yourself.`,
  },
  variables: {
    title: 'Variables',
    unavailable: 'Open a blueprint or deployment to see its variables.',
    views: { label: 'View', variables: 'Variables', secrets: 'Secrets' },
    scope: {
      label: 'Scope',
      all: 'all resources',
      several: (n: number) => `${n} resources`,
      inDeployment: (name: string) => `in ${name}`,
      design: (version: number | null) => (version === null ? '· design' : `· design (v${version})`),
      menu: 'Scope of the Variables window',
      allItem: 'All resources',
      pin: 'Pin scope',
      unpin: 'Unpin scope',
      pinnedHint: 'Pinned: the scope doesn’t follow the selection.',
    },
    toolbar: {
      search: 'Filter by name, source or value',
      refresh: 'Refresh',
      refreshing: 'Refreshing…',
      columns: 'Columns',
      more: 'More',
      sourceTypes: 'Source types…',
      addVariable: 'Add variable',
      secretValuesHidden: 'Secret values hidden',
      secretValuesHiddenHelp:
        'Secret values are write-only in this installation: set them here; Nebisys never shows them.',
      listedAgo: (when: string) => `Vault listed ${when}`,
      readOnly: 'Read-only: you have the reader role.',
      clearFilters: 'Clear filters',
    },
    filter: {
      source: 'Source',
      kind: 'Kind',
      status: 'Status',
      resource: 'Resource',
      group: 'Group',
      all: 'all',
      issues: 'Issues only',
      notSecret: 'not secret',
      chip: (label: string, value: string) => `${label}: ${value}`,
      groupBy: { resource: 'resource', source: 'source', none: 'none' },
      inRun: 'Resources in this run',
      removeInRun: 'Show all resources',
    },
    columns: {
      select: 'Select',
      variable: 'Variable',
      source: 'Source',
      nameInSource: 'Name in source',
      value: 'Current value in source',
      status: 'Status',
      resource: 'Resource',
      lastChanged: 'Last changed',
      version: 'Version',
      usedBy: 'Used by',
      delivery: 'Delivery',
      rotation: 'Rotation',
      actions: 'Actions',
      secretInSource: 'Secret in source',
      valueShort: 'Value',
      expires: 'Expires',
      tags: 'Tags',
    },
    table: {
      label: 'Variables',
      secretsLabel: 'Secrets',
      selectRow: (name: string) => `Select ${name}`,
      selectAll: 'Select all visible rows',
      fromProperty: (pointer: string) => `from property ${pointer}`,
      notEnv: 'not an environment variable',
      group: (resource: string, type: string, count: number) =>
        `${resource} ${type} · ${count} ${count === 1 ? 'variable' : 'variables'}`,
      variablesCount: (n: number) => `${n} ${n === 1 ? 'variable' : 'variables'}`,
      collapse: (resource: string) => `Collapse ${resource}`,
      expand: (resource: string) => `Expand ${resource}`,
      moreStatuses: (n: number) => `+${n}`,
      rowMenu: (name: string) => `Actions for ${name}`,
      sortAscending: 'sorted ascending',
      providerDefined: 'provider-defined names',
      lock: 'secret',
      noRows: 'No variable matches the filters.',
      count: (shown: number) => `${shown} ${shown === 1 ? 'row' : 'rows'}`,
    },
    value: {
      setPerDeployment: 'Set per deployment',
      setPerDeploymentLabel: 'Secret value, set per deployment',
      resolvedAtApply: 'resolved at apply',
      setAtApply: 'set at apply',
      setPerDeploymentParam: 'set per deployment',
      applied: (value: string) => `applied: ${value}`,
      removed: 'Removed on next apply',
      set: 'Set',
      outsideNebisys: 'outside Nebisys',
      byNebisys: 'by Nebisys at apply · read-only',
      noValue: 'No value yet',
      inStore: (store: string) => `In store ${store}`,
      copiedAtLastApply: 'copied at the last apply',
      copiedDetail: (version: number | null, when: string) =>
        version === null ? `(${when})` : `(v${version}, ${when})`,
      generated: 'Generated · copied at the last apply',
      unmanaged: 'Not managed by Nebisys',
      deleted: (when: string) => `Deleted ${when}`,
      inCloud: (value: string) => `in the cloud: ${value}`,
      removedInCloud: 'removed in the cloud',
      recoverableUntil: (date: string) => `recoverable until ${date}`,
      recoverabilityUnknown: 'recoverability unknown',
      notRecoverable: 'can’t be recovered',
      vaultUnreachable: 'Vault unreachable',
      unknown: 'Unknown',
      label: {
        value: (value: string) => `Value ${value}`,
        secretSet: (when: string, by: string | null) =>
          `Secret value, write-only in this installation, set ${when}${by ? ` by ${by}` : ' outside Nebisys'}`,
        secretSystem: 'Secret value, set by Nebisys at apply, read-only',
        noValue: 'Secret without a value yet',
        storeCopy: (store: string) => `In store ${store}, copied at the last apply`,
        generated: 'Generated, copied at the last apply',
        unmanaged: 'Not managed by Nebisys',
        deleted: (when: string) => `Secret deleted ${when}`,
        unreachable: 'Vault unreachable, value state unknown',
        unknown: 'Value state unknown',
      },
    },
    status: {
      'no-value': 'No value yet',
      disabled: 'Disabled',
      'deleted-outside': 'Deleted outside',
      'access-denied': 'Access denied',
      'not-found': 'Vault or secret not found',
      invalid: 'Invalid reference',
      'vault-unreachable': 'Vault unreachable',
      'changed-outside': 'Changed outside',
      'cloud-changed': 'Changed in the cloud',
      expired: 'Expired',
      'tags-edited': 'Tags edited',
      'plain-in-cloud': 'Plain in the cloud',
      unreachable: 'Vault didn’t answer',
      changed: 'Changes on next apply',
      added: 'Added on next apply',
      removed: 'Removed on next apply',
      'not-applied': 'Not applied',
      unsaved: 'Unsaved',
      'stale-value': 'Previous value',
      pending: 'Not resolved yet',
      resolved: 'Resolved',
      applied: 'Applied',
      copied: 'Copied at the last apply',
      'read-only': 'Read-only',
      unknown: 'Unknown',
      'name-conflict': 'Name conflict',
      'rotation-conflict': 'Rotation conflicts',
      rotated: 'Rotated',
      'older-version': 'Uses an older version',
      'rolling-out': 'Rolling out',
      'request-not-attached': 'Not attached',
      'request-unresolved': 'No target',
      'request-ambiguous': 'Choose the target',
      'request-blocked': 'Blocked',
      inSync: 'In sync',
      help: {
        'no-value': 'Plan and apply fail with SECRET_VALUE_MISSING until a value is set.',
        disabled: (app: string) =>
          `Disabled · ${app} loses the value at its next restart or within 24 hours.`,
        'deleted-outside': 'The secret was deleted outside Nebisys.',
        reference: (app: string) => `${app} currently receives the reference text instead of the value.`,
        'vault-unreachable': 'The vault couldn’t be listed, so this secret’s state is unknown.',
        'changed-outside':
          'The current version wasn’t written by Nebisys (for example in the cloud console). Accept it to record it as known, or set a new value.',
        cloudChanged: (ago: string, applied: string | null) =>
          `Changed in the cloud · found by refresh ${ago} · the next apply sets it back${applied === null ? '' : ` to ${applied}`}.`,
        cloudRemoved: (ago: string) =>
          `Removed in the cloud · found by refresh ${ago} · the next apply sets it again.`,
        grantsDelay:
          'Role assignments can take up to 10 minutes to take effect. Refresh references after that.',
        expired: 'Expiry date passed · the value still works.',
        'tags-edited': 'Nebisys’s tags on this secret were changed outside Nebisys. Rewrite them.',
        unreachable:
          'The platform couldn’t reach the vault the last time it resolved the reference. Usually transient.',
        changed:
          'The latest blueprint version or the deployment’s settings give this variable another value.',
        added: 'Only in the latest blueprint version: the next apply adds it.',
        removed: 'Not in the latest blueprint version: the next apply removes it.',
        'not-applied': 'The app isn’t deployed yet.',
        unsaved: 'The open blueprint has unsaved changes to this variable.',
        staleValue: (app: string, until: string) => `${app} may use the previous value until ${until}.`,
        pending: 'The platform hasn’t resolved the reference yet.',
        resolved: (ago: string) => `The platform’s last resolution, read ${ago}. It is not a live check.`,
        applied: 'The deployed value matches the latest blueprint version.',
        'read-only': 'Set by the platform connector; read-only.',
        unknown: 'The platform reported an unknown state.',
        restarts: (app: string) => `Changing app settings restarts ${app}.`,
      },
    },
    strips: {
      notDeployed: (app: string, deployment: string) =>
        `${app} isn’t deployed in ${deployment} yet. Values appear after the first apply.`,
      vaultNotCreated: (deployment: string) =>
        `${deployment}’s vault doesn’t exist yet. Prepare it to set secret values before the first apply.`,
      store: (deployment: string, store: string) =>
        `Secrets of ${deployment} come from the secret store ${store}, read by the Nebisys server at apply time, and are copied into the apps as plain settings. A change in the store reaches the apps at the next apply.`,
      listing: (code: string, message: string) => `${code}: ${message}`,
      staleSince: (ago: string) => `Showing data from ${ago}.`,
      noListing: 'Secret metadata is unknown.',
      throttled: (ago: string) => `The vault throttled reads; showing data from ${ago}.`,
      designError: (deployment: string, message: string) =>
        `The latest version doesn’t resolve with ${deployment}’s settings: ${message}. Sync states are unknown.`,
      lastApplyFailed: (version: number | null) =>
        `The last apply failed: some apps may already have ${version === null ? 'the latest version' : `v${version}`}’s settings.`,
      referenceStatusError: (error: string) => `Couldn’t read reference status from the platform: ${error}`,
      runSnapshot: (deployment: string) => `Current values of ${deployment}, not a snapshot of this run.`,
      cloudAdded: (app: string, names: string, ago: string) =>
        `${app} has settings that only the cloud has: ${names} (found by refresh ${ago}).`,
    },
    empty: {
      app: (app: string) => `${app} has no variables yet.`,
      connections: 'Connections and triggers add variables too.',
      none: 'No apps in scope.',
      noApps: 'This blueprint has no apps yet. Web apps and function apps have variables.',
      noSecrets: 'This blueprint declares no secrets.',
      noDeclarations: 'This blueprint declares no secrets.',
    },
    actions: {
      setValue: 'Set value',
      setAgain: 'Set a value again',
      accept: 'Accept',
      acceptThis: 'Accept this version',
      ignore: 'Ignore',
      rewriteTags: 'Rewrite tags',
      addDeclaration: 'Add declaration',
      edit: 'Edit',
      editInDesigner: 'Edit in designer',
      changeSource: 'Change source…',
      rename: 'Rename',
      remove: 'Remove',
      details: 'Details',
      copyValue: 'Copy value',
      copyName: 'Copy name',
      copyNameInSource: 'Copy name in source',
      openConsole: 'Open in console',
      goToResource: 'Go to resource',
      goToConnection: 'Go to connection',
      prepareVault: 'Prepare vault',
      retry: 'Retry',
      parameterHint: 'Set per deployment in the deployment’s settings.',
      copied: (what: string) => `${what} copied.`,
      editValue: 'Edit value',
      recover: 'Recover',
      recovering: 'Recovering…',
      recoverHelp: 'Restores the previous value and its versions.',
      refreshReferences: 'Refresh references',
      refreshRefs: 'Refresh refs',
      refreshingReferences: 'Refreshing…',
      showActivity: 'Show activity',
    },
    maintenance: {
      recovered: (name: string) => `${name} recovered.`,
      recoveryPending: (name: string) => `Still recovering ${name}. Refresh in a minute.`,
      refreshed: (app: string) => `References of ${app} refreshed. Resolution can finish a moment later.`,
      useNowAction: (label: string) => `Use Set value with “${label}” instead.`,
      refreshApp: (app: string) => `Refresh references of ${app}`,
    },
    parameter: {
      label: (name: string) => `Value of parameter ${name}`,
      note: (name: string) => `Takes effect on the next apply and affects every variable that uses ${name}.`,
      useDefault: (value: string) => `Default (${value})`,
      required: 'Enter a value: the parameter has no default.',
      notANumber: 'Enter a number.',
      notABoolean: 'Choose true or false.',
      notAllowed: 'Choose one of the allowed values.',
      conflict: (name: string, value: string) => `${name} was changed to “${value}” since you opened it.`,
      conflictRemoved: (name: string) => `${name} was removed since you opened it.`,
      overwrite: 'Save again to replace it with your value.',
      save: 'Save',
      cancel: 'Cancel',
      saving: 'Saving…',
      saved: (name: string, value: string) => `${name} set to ${value}. It takes effect on the next apply.`,
      savedDefault: (name: string) => `${name} uses its default again. It takes effect on the next apply.`,
      notDeclared: (name: string) => `The latest blueprint version doesn’t declare the parameter ${name}.`,
    },
    bulk: {
      label: 'Bulk actions',
      selected: (n: number) => `${n} selected`,
      accept: (n: number) => `Accept outside changes (${n})`,
      ignore: (n: number) => `Ignore unmanaged (${n})`,
      rewriteTags: 'Rewrite tags',
      copyNames: 'Copy names',
      remove: (n: number) => `Remove from design (${n})`,
      clear: 'Clear',
      progress: (verb: string, done: number, total: number) => `${verb} ${done} of ${total}…`,
      accepting: 'Accepting',
      ignoring: 'Ignoring',
      summary: (ok: number, verb: string, failed: string[]) =>
        failed.length === 0
          ? `${ok} ${verb}.`
          : `${ok} ${verb}, ${failed.length} failed: ${failed.join('; ')}`,
      accepted: 'accepted',
      ignored: 'ignored',
      namesCopied: (n: number) => `${n} ${n === 1 ? 'name' : 'names'} copied.`,
    },
    details: {
      title: (name: string) => `Details of ${name}`,
      close: 'Close details',
      back: 'Back to the list',
      source: 'Source',
      container: 'Container',
      nameInSource: 'Name in source',
      nativeId: 'Native ID',
      account: 'Account',
      region: 'Region',
      consoleLink: 'Open in console',
      currentValue: 'Current value',
      version: 'Version',
      current: 'Current',
      lastWritten: (id: string, who: string, when: string, accepted: boolean) =>
        `Last version Nebisys wrote: ${id} (${who}, ${when})${accepted ? ', accepted' : ''}`,
      status: 'Status',
      delivery: (app: string) => `Delivery to ${app}`,
      readByServer: 'Read by the Nebisys server at apply time.',
      lastResolution: (ago: string) => `last resolution, read ${ago}`,
      declaration: 'Declaration',
      recentActivity: 'Recent activity',
      showAllActivity: 'Show all activity',
      onChange: (policy: string) => `On change: ${policy}`,
      generate: (length: number) => `Generated · ${length}`,
      usedBy: 'Used by',
      tags: 'Tags',
      nebisysTags: 'Nebisys’s tags',
      userTags: 'Other tags',
      noTags: 'No tags',
      lastApplied: (version: number | null, when: string, by: string | null) =>
        `Last applied with ${version === null ? 'the applied version' : `v${version}`}, ${when}${by ? ` by ${by}` : ''}`,
      rawValue: 'Value in the blueprint',
      problems: 'Problems',
      noIssues: 'No issues.',
      expression: 'Expression',
    },
    setValue: {
      title: (secret: string) => `Set the value of ${secret}`,
      value: 'New value',
      show: 'Show while typing',
      size: (used: string, max: string, limit: string) => `${used} of ${max} (${limit})`,
      tooLarge: 'The value is larger than the limit.',
      required: 'Enter a value.',
      help: 'Written straight to the vault as a new version. Nebisys never stores it.',
      submit: 'Set value',
      cancel: 'Cancel',
      disruption: (text: string) => `(${text})`,
      noApps: 'No app uses this secret yet.',
      success: (secret: string) => `Value of ${secret} set`,
      restarted: (apps: string) => `${apps} restarted.`,
      restartFailed: (apps: string) =>
        `Value set. ${apps} didn’t restart. It picks up the value within 24 hours, at the next apply or a restart.`,
      vaultFirst: 'Prepare the vault first.',
      keys: 'Ctrl+Enter sets the value, Esc cancels.',
    },
    edit: {
      addTitle: 'New variable',
      resource: 'App',
      name: 'Name',
      nameHelp: 'Letters, digits and underscores; starts with a letter or underscore.',
      nameInvalid: 'Use letters, digits and underscores, not starting with a digit (at most 128).',
      nameTaken: 'The app has a variable with this name already.',
      source: 'Source',
      chooseSource: 'Choose a source',
      groups: {
        blueprint: 'Value in the blueprint',
        deployment: 'Value per deployment, kept secret',
        connections: 'Added by connections (read-only)',
      },
      sources: {
        plain: 'Plain value or expression',
        parameter: 'Parameter',
        parameterHint: 'set per deployment',
        output: 'Output of another resource',
        secret: 'Secret',
        secretHint: 'the deployment’s vault, or its secret store',
        connection: 'Connection values',
        connectionHint: 'draw a connects-to',
        trigger: 'Trigger values',
        triggerHint: 'draw a triggers',
      },
      value: 'Value',
      valueOf: (name: string) => `Value of ${name}`,
      parameter: 'Parameter',
      newParameter: 'New parameter…',
      newParameterName: 'New parameter name',
      parameterInvalid: 'Use letters, digits and underscores, starting with a letter.',
      parameterTaken: 'A parameter with this name exists already.',
      outputResource: 'Resource',
      output: 'Output',
      noOutputs: 'No other resource has outputs.',
      secret: 'Secret',
      newSecret: 'New secret…',
      newSecretName: 'New secret name',
      description: 'Description',
      onChange: 'When the value changes',
      generate: 'Generate the value',
      length: 'Length',
      createSecret: (name: string) => `Create secret ${name} and use it`,
      plainNotCopied:
        'The plain value isn’t copied into any vault. Set the secret’s value in each deployment after you save.',
      add: 'Add',
      apply: 'Apply',
      cancel: 'Cancel',
      rename: (name: string) => `Rename ${name}`,
      editValue: (name: string) => `Edit ${name}`,
      changeSourceTitle: (name: string) => `Change the source of ${name}`,
      removed: (name: string) => `${name} removed. Undo with Ctrl+Z.`,
    },
    secretsView: {
      vault: 'Vault',
      placement: (region: string) => `placement ${region}`,
      listed: (ago: string) => `listed ${ago}`,
      console: 'Open in console',
      inSync: 'In sync',
      tagsButton: (n: number) => `${n} ${n === 1 ? 'tag' : 'tags'}`,
      tagsOf: (name: string) => `Tags of ${name}`,
      usedByNone: 'not used',
      declared: 'declared',
      system: 'system',
      unmanaged: 'unmanaged',
      ignored: 'ignored',
      generated: 'generated',
      selectSecret: (name: string) => `Show where ${name} is used`,
      designHeading: 'Secret declarations',
      onChange: 'On change',
      generate: 'Generate',
      usedByVariables: 'Used by',
      none: 'none',
    },
    sourceTypes: {
      title: 'Source types',
      locations: 'Location types',
      locationsHelp: 'Where the value lives, from the vault provider or the secret store.',
      deliveries: 'Delivery platforms',
      deliveriesHelp: 'How the app gets the value, from the app’s connector.',
      type: 'location.type',
      label: 'Label',
      secret: 'Secret',
      setValue: 'Set value',
      limit: 'Max size',
      softDelete: 'Soft delete',
      versions: 'Versions',
      platform: 'delivery.platform',
      mode: 'Mode',
      pickup: 'New value reaches the app',
      nowAction: '“Now” action',
      fallback:
        'Types the frontend doesn’t know yet use a generic entry with the server’s label and texts, so new stores and platforms work before they get a tailored entry.',
    },
    locationLabels: {
      plain: 'Plain value',
      expression: 'Expression',
      parameter: 'Parameter',
      output: (id: string) => `Output · ${id}`,
      connection: (id: string) => `Connection · ${id}`,
      trigger: (id: string) => `Trigger · ${id}`,
      platform: 'Platform setting',
      secret: 'Secret',
      azureKeyVault: 'Azure Key Vault',
      store: (name: string) => `Secret store · ${name}`,
      pulumiState: 'Pulumi state',
    },
    nameLabels: {
      template: 'Template',
      parameter: 'Parameter',
      output: 'Output',
      setting: 'Setting',
      secretName: 'Secret name',
      storeName: 'Name in the store',
      generic: 'Name in source',
    },
    limits: { keyVault: 'Key Vault limit', generic: 'limit' },
    delivery: {
      appService: { reference: 'Key Vault reference · App Service', plain: 'App setting · App Service' },
      flex: { reference: 'Key Vault reference · Flex Consumption', plain: 'App setting · Flex Consumption' },
      pickupReference: (platform: string) =>
        `${platform} picks up the new value within 24 hours, or at the next apply or restart.`,
      pickupPlain: (apps: string) => `${apps} gets the value at the next apply.`,
      restartNow: (apps: string) => `Restart ${apps} now`,
      refreshAppService: (app: string) =>
        `Asks App Service to read ${app}’s Key Vault references again; ${app} may restart.`,
      refreshGeneric: (app: string) => `Asks the platform to read ${app}’s vault references again.`,
      restartDisruption: (several: boolean) =>
        several
          ? 'each restarts all its instances; requests can fail briefly'
          : 'it restarts all its instances; requests can fail briefly',
      readers: (app: string) =>
        `Anyone allowed to read ${app}’s app settings (Owner, Contributor, Website Contributor, which includes your deployment pipeline’s identity) and any code running in ${app} can read it. It’s also in Pulumi state, encrypted.`,
      modes: { reference: 'reference', plain: 'plain', 'runtime-read': 'runtime read' },
    },
    kinds: { declared: 'declared', system: 'system', generated: 'generated', unmanaged: 'unmanaged' },
    card: {
      title: 'Variables',
      label: 'Variables summary',
      summary: (n: number) => `${n} ${n === 1 ? 'variable' : 'variables'}`,
      fromSource: (n: number, label: string) => `${n} from ${label}`,
      secrets: (n: number) => `${n} ${n === 1 ? 'secret' : 'secrets'}`,
      show: 'Show in Variables window',
      issues: (danger: number, warning: number) =>
        [
          danger > 0 ? `${danger} ${danger === 1 ? 'error' : 'errors'}` : '',
          warning > 0 ? `${warning} ${warning === 1 ? 'warning' : 'warnings'}` : '',
        ]
          .filter(Boolean)
          .join(', '),
    },
    footer: (n: number) => `${n} ${n === 1 ? 'variable' : 'variables'}`,
    footerLabel: (resource: string, n: number) =>
      `Show the ${n} ${n === 1 ? 'variable' : 'variables'} of ${resource} in the Variables window`,
    vaultNode: {
      show: 'Show in Variables window',
      help: 'The vault’s secrets are listed in the Variables window’s Secrets view.',
    },
  },
  /** The opt-in reveal of secret values (spec 6.10.18). */
  reveal: {
    button: 'Reveal',
    buttonLabel: (name: string) => `Reveal the value of ${name}`,
    buttonHint: 'Reveal value · recorded in the activity log · Enter',
    revealing: 'Revealing…',
    mask: '••••••••',
    maskedLabel: (when: string, by: string | null) =>
      `Secret value hidden, set ${when}${by ? ` by ${by}` : ' outside Nebisys'}`,
    needsRole: (roles: string) => `Revealing values needs the secret-reader role. Your roles: ${roles}.`,
    noRoles: 'none',
    disabledNote: 'disabled, not revealable',
    unappliedNote: 'revealable after the next apply',
    unappliedHelp:
      'Only secrets the last applied blueprint version declares can be revealed. Apply the deployment first.',
    systemNote: 'system secrets aren’t revealable here',
    systemHelp:
      'System secrets can’t be revealed here. Use your cloud’s tools if you need the database password.',
    confirmTitle: (name: string) => `Reveal ${name}?`,
    confirmCopyTitle: (name: string) => `Copy the value of ${name}?`,
    readsAs: 'as the deployment’s cloud account',
    hidesAfter: 'The value hides again after 30 seconds.',
    copyNotShown: 'The value goes to the clipboard without being shown.',
    keyVault: {
      before: 'Nebisys records the reveal with your name, then reads the current value from Azure Key Vault',
      after:
        'If the vault’s diagnostic logs are enabled, Azure logs the read under that account with the activity entry’s ID as request reference.',
      disabled: 'Key Vault refuses to read a disabled secret.',
    },
    generic: {
      before: (label: string) =>
        `Nebisys records the reveal with your name, then reads the current value from ${label}`,
      disabled: 'The vault refuses to read a disabled secret.',
    },
    reason: 'Reason',
    reasonOptional: 'optional · stored in the activity log',
    reasonRequired: 'required in this installation',
    reasonPlaceholder: 'e.g. INC-4211 payment errors',
    reasonMissing: 'A reason is required in this installation.',
    reasonCount: (n: number) => `${n} of 200`,
    dontAsk: 'Don’t ask again until I sign out',
    cancel: 'Cancel',
    confirm: 'Reveal',
    confirmCopy: 'Copy value',
    copy: (name: string) => `Copy the value of ${name}`,
    copyShort: 'Copy',
    hideShort: 'Hide',
    hide: (name: string) => `Hide the value of ${name}`,
    expand: 'Expand',
    expandLabel: (name: string) => `Show the whole value of ${name}`,
    viewerTitle: (name: string) => `Value of ${name}`,
    close: 'Close',
    seconds: (n: number) => `${n} s`,
    secondsLabel: (n: number) => `Hides in ${n} ${n === 1 ? 'second' : 'seconds'}`,
    revealedLabel: (name: string) => `Revealed value of ${name}:`,
    version: (version: string) => `version ${version}`,
    entry: (ref: string) => `activity entry ${ref}`,
    entryTitle: (version: string, id: string) => `Version ${version} · activity entry ${id}`,
    outside: 'written outside Nebisys',
    hiddenAgain: (name: string) => `${name} hidden again`,
    copiedShown: 'Value copied. Clipboard history and clipboard sync keep copies.',
    copiedRecorded:
      'Value copied and recorded in the activity log. Clipboard history and clipboard sync keep copies.',
    copyFailed: 'The value wasn’t copied.',
    noClipboard: 'This browser doesn’t allow copying here.',
    retry: 'Retry',
    errors: {
      auditShort: 'Not shown: activity log unavailable',
      audit: 'Not shown: the activity log is unavailable, so the reveal wasn’t recorded. Try again.',
      rateLimited: 'Too many reveals. Try again in a minute.',
      rateLimitedFor: (seconds: number) =>
        `Too many reveals. Try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`,
      unexpectedShort: 'Not shown: the reveal failed',
      unexpected: 'Not shown: the answer couldn’t be read. Try again.',
      copyUnexpected: 'The clipboard or the answer failed. Try again.',
      disabled: 'Revealing secret values is turned off in this installation.',
      forbidden: 'Revealing values needs the secret-reader role.',
    },
  },
  parameters: {
    add: 'Add parameter',
    name: 'Name',
    type: 'Type',
    default: 'Default',
    allowed: 'Allowed values',
    allowedHelp: 'Comma-separated. Leave empty to allow any value.',
    minimum: 'Minimum',
    maximum: 'Maximum',
    description: 'Description',
    remove: (name: string) => `Remove parameter ${name}`,
    none: 'No parameters.',
    requiredHint: 'No default: every deployment must set it.',
    nameInvalid: 'Start with a letter; letters, digits and underscores (at most 63).',
    nameTaken: 'Another parameter has this name.',
  },
  form: {
    expression: 'Expression',
    useExpression: (field: string) => `Use an expression for ${field}`,
    expressionPlaceholder: '${param.name}',
    completions: 'Suggestions',
    secretName: 'Secret name',
    secretNameHelp: 'The name of a secret in the deployment’s secret store, written as ${secret.<name>}.',
    addEntry: 'Add entry',
    removeEntry: 'Remove entry',
    key: 'Key',
    value: 'Value',
    addItem: 'Add item',
    removeItem: 'Remove item',
    moveUp: 'Move up',
    moveDown: 'Move down',
    requiredMark: 'required',
    secretCompletion: 'type the secret name',
  },
  conflict: {
    title: 'The blueprint was changed by someone else',
    message: (version: number, user: string, time: string) =>
      `Version ${version} was saved by ${user} at ${time}.`,
    review: 'Review differences',
    discard: 'Discard mine',
    saveAnyway: 'Save anyway',
    mine: 'Mine',
    theirs: (version: number) => `Theirs (version ${version})`,
    hideDiff: 'Hide differences',
  },
  versions: {
    title: 'Versions',
    version: 'Version',
    created: 'Created',
    hash: 'Content hash',
    compare: 'Compare',
    compareHelp: 'Select two versions to compare them side by side.',
    openReadOnly: 'Open read-only',
    selectVersion: (v: number) => `Select version ${v}`,
    noDifferences: 'The versions are identical.',
    left: (v: number) => `Version ${v}`,
  },
  diff: {
    added: 'added',
    removed: 'removed',
    modified: 'changed',
    legend: 'Lines marked + were added, − removed, ~ changed.',
  },
  deployments: {
    title: 'Deployments',
    create: 'New deployment',
    name: 'Name',
    status: 'Status',
    drift: 'Drift',
    version: 'Version',
    lastApply: 'Last apply',
    activeRun: 'Active run',
    updateAvailable: 'update available',
    empty: 'No deployments yet.',
    openRun: 'Open run',
    needsRefresh: 'The next plan will refresh state first.',
    needsRecovery: 'The last run was interrupted. Locks will be cleared before the next run.',
    currentVersion: 'Blueprint version',
    plan: 'Plan',
    apply: 'Apply',
    refresh: 'Refresh',
    destroy: 'Destroy',
    editSettings: 'Edit settings',
    delete: 'Delete',
    deleteOnly: 'Only new or destroyed deployments can be deleted.',
    deleteConfirm: (name: string) => `Delete the deployment “${name}”? This can’t be undone.`,
    runActive: 'A run is active.',
    resources: 'Resources',
    runs: 'Runs',
    noResources: 'No resources have been deployed.',
    noRuns: 'No runs yet.',
    resourceId: 'Resource',
    type: 'Type',
    provider: 'Provider',
    region: 'Region',
    primaryCloudId: 'Primary cloud ID',
    copyCloudId: (id: string) => `Copy the cloud ID of ${id}`,
    outputs: 'Outputs',
    kind: 'Kind',
    triggeredBy: 'Triggered by',
    created: 'Created',
    duration: 'Duration',
    blueprint: 'Blueprint',
    allRuns: 'All runs',
    // Lists (spec 6.4)
    listSubtitle: 'Every environment of every blueprint.',
    blueprintSubtitle: 'Blueprint',
    search: 'Filter by name',
    blueprintFilter: 'Blueprint',
    statusFilter: 'Status',
    driftFilter: 'Drift',
    providerFilter: 'Provider',
    providers: 'Providers',
    noMatch: 'No deployment matches the filters.',
    emptyAll: 'No deployments yet. Create one from a blueprint’s deployments.',
    goToBlueprints: 'Go to Blueprints',
    activeRunLabel: (kind: string, progress: string | null) => (progress ? `${kind} · ${progress}` : kind),
    filters: 'Filters',
  },
  runsList: {
    title: 'Runs',
    subtitle: 'Plans, applies, refreshes and destroys of every deployment, newest first.',
    statusFilter: 'Status',
    kindFilter: 'Kind',
    deploymentFilter: 'Deployment',
    statusValues: {
      active: 'Running and queued',
      completed: 'Completed',
      failed: 'Failed',
      canceled: 'Canceled',
    },
    active: 'Running and queued',
    history: 'History',
    nothingRunning: 'Nothing is running.',
    empty: 'No runs yet.',
    noMatch: 'No run matches the filters.',
    run: 'Run',
    deployment: 'Deployment',
    status: 'Status',
    triggeredBy: 'Triggered by',
    version: 'Version',
    started: 'Started',
    duration: 'Duration',
    changes: 'Changes',
    queued: 'Queued',
    rollbackOf: 'Rollback of',
    parentRun: 'the parent run',
    unknown: 'Unknown',
    unknownTitle: 'The change counts of this run weren’t recorded.',
    noChanges: 'No changes',
    warnings: (n: number) => `${n} ${n === 1 ? 'warning' : 'warnings'}`,
    vaultOnly: 'Vault only',
    changeCount: (label: string, n: number) => `${n} to ${label.toLowerCase()}`,
    filters: 'Filters',
  },
  settings: {
    createTitle: 'New deployment',
    editTitle: (name: string) => `Settings of ${name}`,
    name: 'Name',
    nameHelp: 'Letters, digits, spaces, hyphens and underscores; at most 64 characters.',
    nameInvalid: 'Use letters, digits, spaces, hyphens and underscores, starting with a letter or digit.',
    defaultPlacement: 'Default placement',
    provider: 'Provider',
    region: 'Region',
    noProvider: 'None',
    parameters: 'Parameters',
    noParameters: 'The blueprint has no parameters.',
    parameterRequired: 'Required: the blueprint gives no default.',
    defaultValue: (value: string) => `Default: ${value}`,
    range: (min?: number, max?: number) =>
      min !== undefined && max !== undefined
        ? `Between ${min} and ${max}.`
        : min !== undefined
          ? `At least ${min}.`
          : `At most ${max}.`,
    placementOverrides: 'Placement overrides',
    placementOverridesHelp: 'Optional. Overrides the blueprint and the default placement per resource.',
    cloudAccounts: 'Cloud accounts',
    cloudAccountsHelp: 'One account for each provider the deployment uses.',
    noProvidersUsed: 'Choose a provider in the default placement or the overrides.',
    chooseAccount: 'Choose an account',
    noAccounts: (provider: string) => `No ${provider} cloud accounts exist. An admin can add one.`,
    missingAccount: (provider: string) => `Choose a cloud account for ${provider}.`,
    secretStore: 'Secret store',
    defaultStore: 'default',
    vault: 'Vault',
    vaultProvider: 'Vault provider',
    vaultRegion: 'Vault region',
    vaultHelp: 'Where the deployment’s secrets live. Empty fields come from the default placement.',
    vaultManaged: 'Managed vault: Nebisys creates it and apps get references to the secrets.',
    vaultFallback:
      'This provider has no managed vault: secret values come from the secret store and are injected as plain settings.',
    fromDefault: 'From the default placement',
    onFailure: 'Failure policy',
    stop: 'Stop',
    rollback: 'Roll back',
    stopHelp: 'Stop at the failure and leave the resources as they are.',
    rollbackHelp:
      'If an apply fails while provisioning, start a new apply with the last successful version and settings (or a destroy if nothing was ever applied).',
    reloaded:
      'Someone else changed this deployment. It was reloaded; the changes are shown below. Review and save again.',
    noChanges: 'Nothing changed.',
    resource: 'Resource',
    fixErrors: 'Fix the marked fields.',
  },
  apply: {
    title: (name: string) => `Apply ${name}`,
    reviewed: 'Apply this plan',
    reviewedSummary: (version: number, time: string, by: string) =>
      `Plan of v${version} from ${time} by ${by}:`,
    reviewedHelp: 'Nebisys plans again first and stops with PLAN_STALE if the changes differ from this plan.',
    unreviewed: 'Apply without a reviewed plan',
    unreviewedHelp: 'Applies the chosen version directly, without a preview.',
    unreviewedGuard: 'Nobody has reviewed what this apply changes.',
    noPlan:
      'There is no reviewed plan for the current settings and latest version. Run a plan first to review the changes.',
    version: 'Blueprint version',
    onFailure: 'Failure policy for this run',
    submit: 'Apply',
    submitReviewed: 'Apply this plan',
    dangerTitle: 'This plan replaces or deletes data:',
    loadingPlan: 'Loading the plan…',
    planUnknown:
      'The reviewed plan couldn’t be loaded, so its dangerous changes aren’t known. Retry, or type the name to apply it anyway.',
    typeName: (name: string) => `Type “${name}” to apply.`,
    typedLabel: 'Deployment name',
  },
  destroy: {
    title: (name: string) => `Destroy ${name}`,
    intro: (name: string) => `This deletes every cloud resource of ${name}.`,
    resources: 'Resources to delete',
    noResources: 'No resources are recorded.',
    dataDeleted: 'data is deleted',
    typeName: (name: string) => `Type “${name}” to confirm.`,
    submit: (name: string) => `Destroy ${name}`,
    vaultSoftDelete: (name: string, days: number, until: string, deployment: string, region: string) =>
      `Vault ${name} goes into soft delete, not purged: its secrets stay recoverable for ${days} days (until ${until}), and applying ${deployment} again in ${region} brings them back. The name stays reserved for ${days} days.`,
    vaultGeneric: (name: string, label: string) =>
      `Vault ${name} is deleted as ${label} deletes vaults; check its recovery rules before you destroy.`,
    genericVaultLabel: 'its cloud',
  },
  deploymentPage: {
    tabs: 'Deployment',
    overview: 'Overview',
    runs: 'Runs',
    settings: 'Settings',
    activity: 'Activity',
    moreActions: (name: string) => `More actions for ${name}`,
    applyEllipsis: 'Apply…',
    editSettings: 'Edit settings',
    destroy: 'Destroy…',
    nothingToDestroy: 'Nothing to destroy.',
    nothingToRefresh: 'Nothing to refresh yet.',
    blueprint: 'Blueprint',
    applied: (version: number, when: string) => `applied v${version} ${when}`,
    appliedBy: (user: string) => ` by ${user}`,
    notApplied: 'not applied yet',
    latest: (version: number) => `latest v${version}`,
    noPlacement: 'no default placement',
    placement: (provider: string, region: string) => `${provider} / ${region}`,
    runBanner: (kind: string, status: 'pending' | 'running') =>
      status === 'pending' ? `${kind} queued` : `${kind} running`,
    runStarted: (when: string, by: string) => `started ${when} by ${by}`,
    cancelEllipsis: 'Cancel…',
    banners: 'Notices',
    failedRun: (kind: string) => `The last ${kind.toLowerCase()} failed:`,
    nextPlan: 'Next: plan the deployment.',
    nextVault: 'Prepare the vault and set secret values first.',
    showSecrets: 'Show secrets',
    planReady: 'A reviewed plan is ready.',
    planReadyDetail: (version: number, when: string, by: string) =>
      `Plan of v${version} from ${when} by ${by}:`,
    settingsUnchanged: 'Settings unchanged since.',
    planOutOfDate: (version: number, when: string, by: string) =>
      `The plan of v${version} from ${when} by ${by} is out of date:`,
    reviewPlan: 'Review plan',
    applyThisPlan: 'Apply this plan',
    health: 'Health',
    lastApply: 'Last apply',
    neverApplied: 'Never applied',
    drift: 'Drift',
    driftSecrets: (n: number) => (n === 1 ? '1 secret changed' : `${n} secrets changed`),
    driftDetected: 'Drift detected',
    noDrift: 'No drift',
    notChecked: 'Not checked yet',
    refreshAt: (when: string) => `Refresh ${when}`,
    resourcesInSync: 'resources in sync',
    resourcesDrifted: (n: number) => (n === 1 ? '1 resource changed' : `${n} resources changed`),
    noRefresh: 'No refresh yet',
    vault: 'Vault',
    vaultReady: (label: string) => `${label} · ready`,
    vaultNotCreated: 'Not created',
    vaultNone: 'No secrets',
    vaultSoftDeleted: (date: string) => `Soft-deleted until ${date}`,
    vaultMissing: (n: number) => (n === 1 ? '1 value missing' : `${n} values missing`),
    vaultStoreHelp: 'Values come from the secret store.',
    vaultCouldNotCheck: 'Couldn’t check in the cloud',
    vaultPurged: (when: string) => `Purged outside Nebisys (found ${when})`,
    triggers: 'Triggers',
    triggersWaiting: (n: number) => (n === 1 ? '1 waiting for code' : `${n} waiting for code`),
    triggersHelp: 'Deploy the code, then apply again',
    noneWaiting: 'None waiting',
    noTriggers: 'No trigger connections',
    resources: 'Resources',
    resourceCount: (n: number) => (n === 1 ? '1 resource' : `${n} resources`),
    filterResources: 'Filter resources',
    noResourceMatch: 'No resource matches the filter.',
    resource: 'Resource',
    placementColumn: 'Placement',
    status: 'Status',
    variables: 'Variables',
    outputs: 'Outputs',
    actions: 'Actions',
    resourceActions: (id: string) => `Actions for ${id}`,
    openInDesigner: 'Open in designer',
    showVariables: 'Show in Variables window',
    refreshReferences: 'Refresh references',
    copyCloudId: 'Copy cloud ID',
    copyOutput: (name: string, id: string) => `Copy ${name} of ${id}`,
    triggerBadge: 'trigger',
    triggerBadgeTitle: 'A trigger from this resource waits for the function code.',
    deployBoxes: 'Function code',
    recentRuns: 'Recent runs',
    allRuns: 'All runs',
    openInRuns: 'Open in Runs',
    kindFilter: 'Kind',
    statusFilter: 'Status',
    noRunMatch: 'No run matches the filters.',
    deleted: (name: string) => `Deleted ${name}.`,
  },
  createDeployment: {
    title: 'New deployment',
    steps: 'Steps',
    stepNames: ['Name and placement', 'Cloud accounts', 'Parameters', 'Secrets and safety'] as const,
    step: (n: number, name: string) => `Step ${n}: ${name}`,
    back: 'Back',
    next: 'Next',
    create: 'Create',
    checking: 'Checking…',
    providerAccounts: 'One account for each provider the deployment uses.',
    noProvidersYet: 'Choose a default provider on the first step.',
    verified: 'verified',
    verificationFailed: 'failed',
    neverVerified: 'never verified',
    newAccount: 'New cloud account',
    vaultPlacement: 'Vault placement',
    sameAsDefault: 'Same as the default placement',
    chooseVault: 'Choose a provider and region',
    vaultRule: 'The vault’s provider and region can’t be changed after it is created.',
    genericVault: 'vault',
    managedVault: (label: string, region: string) => `Secrets go to a managed ${label} in ${region}.`,
    storeFallback: (provider: string) =>
      `No managed vault on ${provider} yet: secrets come from the secret store, read by the Nebisys server at apply time, and are copied into the apps as plain settings.`,
    appsElsewhere: (regions: string, vaultRegion: string) =>
      `Apps in ${regions} read their secrets from ${vaultRegion} at start-up.`,
    advanced: 'Advanced: placement overrides',
    stepErrors: 'Fix the marked fields.',
    otherProblems: 'The check found these problems:',
  },
  deploymentSettings: {
    nav: 'Settings sections',
    leaveTitle: 'Leave without saving?',
    leaveMessage: 'Your changes to the deployment’s settings will be lost.',
    sections: {
      general: 'General',
      placement: 'Placement',
      parameters: 'Parameters',
      accounts: 'Cloud accounts',
      vault: 'Secrets & vault',
      failure: 'Failure policy',
    },
    changed: 'changed',
    sectionChanged: (name: string) => `${name}, changed`,
    readOnly: 'You need the operator role to change the settings.',
    defaultProvider: 'Default provider',
    defaultRegion: 'Default region',
    was: (value: string) => `was ${value}`,
    effective: 'Effective placement',
    comesFrom: 'Comes from',
    sources: {
      override: 'override in this deployment',
      blueprint: 'blueprint',
      default: 'deployment default',
      none: 'missing',
    },
    deployed: 'deployed',
    override: 'Override…',
    editOverride: 'Edit',
    overrideFor: (id: string) => `Override the placement of ${id}`,
    removeOverride: 'Remove override',
    doneOverride: 'Done',
    providerOf: (id: string) => `Provider of ${id}`,
    regionOf: (id: string) => `Region of ${id}`,
    vaultStays: (vaultRegion: string, regions: string) =>
      `The vault stays in ${vaultRegion}. Apps in ${regions} read their secrets from ${vaultRegion} at start-up. Moving the vault needs a new deployment.`,
    consequencesTitle: (region: string | null, n: number) =>
      region
        ? `Moving to ${region} recreates ${n} ${n === 1 ? 'resource' : 'resources'}.`
        : `The new placement recreates ${n} ${n === 1 ? 'resource' : 'resources'}.`,
    checking: 'Checking the placement…',
    checkFailed: 'The placement couldn’t be checked.',
    fixed: 'fixed',
    vault: 'Vault',
    vaultPlacement: 'Placement',
    state: 'State',
    secretStore: 'Secret store',
    storeOnlyWithoutVault: (store: string) => `${store} (used only without a managed vault)`,
    softDeleted: (date: string) => `Soft-deleted · kept until ${date}`,
    couldNotCheck: (message: string) => `Couldn’t check the vault in the cloud: ${message}`,
    vaultPurged: (date: string) =>
      `The soft-deleted vault was purged outside Nebisys (found ${date}). Its name is free and the placement can be chosen again: the next apply creates a new, empty vault.`,
    vaultRunActive: (region: string) =>
      `A run of this deployment is in progress and may create the vault in ${region}. The vault’s placement can’t change until the run ends.`,
    fixedText: (name: string, region: string) =>
      `${name} stays in ${region}. Moving secrets to another region needs a new deployment: vault names stay reserved for up to 90 days and values aren’t copied.`,
    saveBar: 'Unsaved settings',
    changes: (n: number) => (n === 1 ? '1 change' : `${n} changes`),
    more: (n: number) => `+${n} more`,
    takesEffect: 'takes effect on the next run',
    typedNote: (name: string) => `the plan will ask you to type ${name} to apply`,
    discard: 'Discard',
    save: 'Save settings',
    saving: 'Saving…',
    saved: 'Settings saved. They take effect on the next run.',
    errorSummary: (n: number) => (n === 1 ? '1 field needs attention:' : `${n} fields need attention:`),
    conflict: (fields: string) =>
      `The deployment’s settings changed since you opened them: ${fields}. Your other changes are kept.`,
    conflictField: (field: string, value: string) => `${field} is now ${value}`,
    rebased: 'The deployment’s settings changed since you opened them. Your changes are kept.',
    fieldNames: {
      name: 'name',
      defaultProvider: 'default provider',
      defaultRegion: 'default region',
      parameter: (name: string) => `parameter ${name}`,
      override: (id: string, field: string) => `${id} ${field} override`,
      account: (provider: string) => `${provider} cloud account`,
      vaultProvider: 'vault provider',
      vaultRegion: 'vault region',
      secretStore: 'secret store',
      onFailure: 'failure policy',
    },
    unset: 'not set',
  },
  impacts: {
    'data-deleted': (ids: string, many: boolean) =>
      `${ids} ${many ? 'are' : 'is'} recreated empty: ${many ? 'their' : 'its'} data is deleted.`,
    'messages-lost': (ids: string, many: boolean) =>
      `${ids} ${many ? 'move' : 'moves'} to a new resource: unread messages and events are lost.`,
    'code-redeploy': (ids: string, many: boolean) =>
      `${ids} ${many ? 'need' : 'needs'} the code deployed again.`,
    outage: (ids: string, many: boolean, region: string | null) =>
      `${ids} ${many ? 'are' : 'is'} recreated${region ? ` in ${region}` : ''}: expect an outage.`,
    'endpoint-changes': (ids: string, many: boolean) =>
      `${ids} ${many ? 'get' : 'gets'} a new address: clients that call ${many ? 'them' : 'it'} need the new one.`,
    'blocked-by-name': (ids: string, many: boolean) =>
      `${ids} ${many ? 'keep their names' : 'keeps its name'}, so the old one is deleted before the new one is created.`,
    recreated: (ids: string, many: boolean, region: string | null) =>
      `${ids} ${many ? 'are' : 'is'} recreated${region ? ` in ${region}` : ''}.`,
    unknown: (impact: string, ids: string) => `${ids}: ${impact}.`,
    statefulDelete: (id: string) => `${id} is deleted with its data.`,
    vaultReplaced: (name: string) => `The vault ${name} is replaced.`,
    vaultDeleted: (name: string) => `The vault ${name} is deleted.`,
    vaultValues: (deployment: string) => `Every secret value of ${deployment} is in this vault.`,
    typedNote: (deployment: string) => `Applying this plan asks you to type ${deployment}.`,
  },
  planReview: {
    current: 'This plan is current.',
    currentDetail: (version: number, hash: string) =>
      `Same blueprint version (v${version}) and the same settings (hash ${hash}) as the deployment now. “Apply this plan” binds the apply to it: Nebisys plans again first and stops with PLAN_STALE if anything differs.`,
    stale: 'This plan is out of date:',
    reasons: {
      notCompleted: 'Only a completed plan can be applied.',
      newerVersion: 'A newer blueprint version exists.',
      settingsChanged: 'Deployment settings changed since this plan.',
      appliedAfter: 'The deployment was applied after this plan.',
      runActive: 'A run is active.',
    },
    staleReasons: {
      newerVersion: (version: number) => `a newer blueprint version exists (v${version})`,
      settingsChanged: (from: string, to: string) =>
        `the deployment’s settings changed since (hash ${from} → ${to})`,
      appliedAfter: 'the deployment was applied after this plan',
    },
    applyThisPlan: 'Apply this plan…',
    planAgain: 'Plan again',
    moreActions: 'More actions for this run',
    copyLink: 'Copy link',
    downloadJson: 'Download JSON',
    linkCopied: 'Link copied.',
    chips: 'Filter changes by action',
    chip: {
      create: (n: number) => `${n} to create`,
      update: (n: number) => `${n} to update`,
      replace: (n: number) => `${n} to replace`,
      delete: (n: number) => `${n} to delete`,
      'no-change': (n: number) => `${n} unchanged`,
    },
    hideUnchanged: 'Hide unchanged',
    dangerTitle: 'Dangerous changes',
    issues: 'Warnings and errors',
    showOnCanvas: 'Show on canvas',
    setMissingValues: 'Set missing values',
    changes: 'Changes',
    action: 'Action',
    resource: 'Resource',
    type: 'Type',
    summary: 'Changes',
    newResource: 'new resource',
    removed: 'removed',
    noChange: 'no change',
    variablesCount: (n: number) => (n === 1 ? '1 variable' : `${n} variables`),
    childrenCount: (n: number, label: string) => `${n} ${label}${n === 1 ? '' : 's'}`,
    restarts: 'restarts',
    noRows: 'No change matches the filter.',
    noChanges: 'The plan changes no resource.',
    expand: (id: string) => `Details of ${id}`,
    variablesHeading: 'Variables',
    added: 'added',
    changed: 'changed',
    removedWord: 'removed',
    reference: (label: string, secret: string) => `${label} reference to ${secret}`,
    valueSet: (when: string) => `value set ${when}`,
    noValue: 'no value yet',
    variablesNote: (version: number | null) =>
      `Variables come from Nebisys’s comparison of ${version !== null ? `v${version} (applied)` : 'the deployment'} with this plan, not from the native diff.`,
    restartsNote: (app: string) => `Changing app settings restarts ${app}.`,
    noSecretValues: 'Secret values never appear in plans.',
    openInVariables: (app: string) => `Open ${app} in the Variables window`,
    notRecorded: 'Variable changes weren’t recorded for this plan.',
    variablesLoading: 'Loading the variable changes…',
    childrenHeading: 'Child resources',
    propertiesHeading: 'Other properties',
    shared: 'Shared infrastructure',
    sharedNote: 'created by Nebisys around your resources',
    sharedUnchanged: (n: number, list: string) => `${n} unchanged, hidden: ${list}.`,
    noShared: 'No shared changes.',
    beforeYouApply: 'Before you apply',
    secretsOk: 'Every secret the plan uses has a value',
    secretsMissing: (n: number) => (n === 1 ? '1 secret has no value' : `${n} secrets have no value`),
    noSecrets: 'The plan uses no secrets.',
    storeSecrets: 'Secrets come from the secret store.',
    noDanger: 'No database, bucket or vault is replaced or deleted',
    danger: (n: number) => (n === 1 ? '1 dangerous change' : `${n} dangerous changes`),
    restartsList: 'Apps that restart:',
    restartReason: '(settings change)',
    noRestarts: 'No app restarts',
    restartsNotRecorded: 'Apps that restart weren’t recorded',
    triggersWaiting: (n: number) => (n === 1 ? '1 trigger waits for code' : `${n} triggers wait for code`),
    failurePolicy: (policy: string) => `Failure policy: ${policy}`,
    variablesPanel: 'Variables this plan changes',
    newValueSet: 'new · value set',
    newNoValue: 'new · no value yet',
    newValue: (value: string) => `new · ${value}`,
    removedValue: 'removed',
    none: 'None.',
    runPanel: 'Run',
    kind: 'Kind',
    triggeredBy: 'Triggered by',
    version: 'Version',
    settings: 'Settings',
    snapshot: 'snapshot',
    hash: (hash: string) => `hash ${hash}`,
    log: 'Log',
    events: (n: number) => (n === 1 ? '1 event in Activity' : `${n} events in Activity`),
  },
  runPage: {
    scopeFull: 'Full scope',
    scopeVault: 'Vault only',
    blueprintVersion: (version: number) => `Blueprint v${version}`,
    boundTo: (time: string) => `Bound to plan ${time}`,
    live: 'Live',
    reconnecting: (time: string) => `Reconnecting… · last event ${time}`,
    deployment: 'Deployment',
    started: (time: string, by: string) => `started ${time} by ${by}`,
    queued: 'queued',
    afterRun: 'After the run it waits for',
    running: (duration: string) => `running ${duration}`,
    policy: (policy: string) => `failure policy ${policy}`,
    snapshot: 'settings snapshot',
    snapshotTitle: 'Settings snapshot',
    snapshotHash: (hash: string) => `Hash ${hash}`,
    compare: (from: number, to: number) => `Compare v${from} (applied) → v${to}`,
    rollbackOf: 'Rollback of',
    rolledBackBy: 'Rolled back by',
    parentRun: 'the parent run',
    rollbackRun: 'a rollback run',
    cancel: 'Cancel run…',
    cancelRequested: 'Cancel requested…',
    cancelTitle: (kind: string) => `Cancel the running ${kind.toLowerCase()}?`,
    cancelText:
      'Nebisys force-stops the operation. Changes already in flight in the cloud may finish without being recorded, so the next plan or apply refreshes state first. Canceled applies are never rolled back.',
    keepRunning: 'Keep running',
    confirmCancel: 'Cancel run',
    progress: 'Progress',
    progressText: (completed: number, total: number) => `${completed} of ${total} resources done`,
    completed: (kind: string) => `${kind} completed.`,
    openDeployment: 'Open deployment',
    vaultDone: 'Vault prepared. Next: set the remaining secret values.',
    setSecretValues: 'Set secret values',
    failed: (kind: string) => `${kind} failed.`,
    canceled:
      'The run was canceled. Changes in flight may have finished without being recorded; the next plan or apply refreshes state first.',
    planAgain: 'Plan again',
    resources: 'Resources',
    actionsFromPlan: (time: string) => `actions from plan ${time}`,
    actionsAfterCompletion: 'Actions show when the run completes.',
    fromPlan: 'from plan, no live status',
    trigger: 'trigger',
    selectHint:
      'Select a resource to filter the log. Status updates arrive live; the list never reorders while running.',
    noResources: 'No resources in this run.',
    statusWords: {
      pending: 'Pending',
      'in-progress': 'In progress',
      completed: 'Completed',
      failed: 'Failed',
      skipped: 'Skipped',
    },
    noChange: 'no change',
    log: 'Log',
    levels: 'Log level',
    levelNames: { debug: 'Debug', info: 'Info', warning: 'Warnings', error: 'Errors' },
    removeResource: (id: string) => `Show every resource (now ${id})`,
    searchLog: 'Search log',
    follow: 'Follow',
    download: 'Download',
    noEvents: 'No events match the filters.',
    footer: (shown: number, total: number, filters: string, zone: string) =>
      `Showing ${shown} of ${total} events (filters: ${filters}). Times in your time zone (${zone}).`,
    filterLevel: (level: string) => `${level.toLowerCase()} and above`,
    filterResource: (id: string) => `resource ${id}`,
    filterSearch: (text: string) => `“${text}”`,
    policyDenial: (cloud: string, policy: string | null) =>
      `${cloud} organization policy${policy ? ` ${policy}` : ''} denied this.`,
    policyDenialText:
      'This is an organization rule in your cloud, not a Nebisys error. Ask your cloud administrator.',
  },
  runs: {
    title: (kind: string) => `${kind} run`,
    kind: 'Kind',
    status: 'Status',
    triggeredBy: 'Triggered by',
    parentRun: 'Parent run',
    rollbackOf: 'rollback of',
    version: 'Blueprint version',
    progress: 'Progress',
    progressValue: (completed: number, total: number) => `${completed} / ${total}`,
    started: 'Started',
    finished: 'Finished',
    cancel: 'Cancel run',
    cancelRequested: 'Cancel requested',
    deployment: 'Deployment',
    plan: 'Plan',
    shared: 'Shared infrastructure',
    noChanges: 'No changes.',
    resource: 'Resource',
    type: 'Type',
    action: 'Action',
    changedProperties: 'Changed properties',
    showProperties: (n: number) => `${n} changed ${n === 1 ? 'property' : 'properties'}`,
    drift: 'Drift',
    noDrift: 'No drift detected.',
    resources: 'Resources',
    noResourceEvents: 'No resource status yet.',
    log: 'Log',
    level: 'Level',
    allLevels: 'All levels',
    allResources: 'All resources',
    filterResource: 'Resource',
    autoScrollPaused: 'Auto-scroll paused.',
    resumeAutoScroll: 'Resume auto-scroll',
    noEvents: 'No events.',
    error: 'Error',
    warnings: 'Warnings',
    diagnostics: 'Diagnostics',
    reconnecting: 'Connection lost. Reconnecting…',
    live: 'Live',
    stateMayBeStale: 'Recorded state may be stale. The next plan or apply refreshes it first.',
    vaultScope: 'Vault only',
    secretDrift: 'Secrets',
    noSecretDrift: 'No secret drift.',
    settingsDrift: 'App settings changed in the cloud',
    noSettingsDrift: 'No app setting changed in the cloud.',
    settingsDriftChange: {
      changed: 'Changed in the cloud',
      added: 'Added in the cloud',
      removed: 'Removed in the cloud',
    },
    settingsNotChecked: (apps: string) => `Not checked: ${apps}.`,
    openVariables: 'Open the deployment’s variables',
    setMissingValues: 'Set the missing values in the secrets view, then apply again.',
    openSecrets: 'Open the secrets view',
  },
  cloudAccounts: {
    title: 'Cloud accounts',
    create: 'New cloud account',
    newShort: 'New',
    name: 'Name',
    provider: 'Provider',
    authMethod: 'Auth method',
    lastVerification: 'Last verification',
    never: 'Never verified',
    verifiedAgo: (when: string) => `Verified ${when}`,
    passed: 'Passed',
    failed: 'Failed',
    unknown: 'Couldn’t check',
    staticKey: 'Static key',
    empty: 'No cloud accounts yet.',
    filter: 'Filter',
    filterLabel: 'Filter cloud accounts',
    noMatch: 'No cloud account matches the filter.',
    choose: 'Choose a cloud account to see its settings, verification and usage.',
    back: 'All cloud accounts',
    scope: 'Scope',
    scopeHelp: 'where Nebisys deploys',
    general: 'General',
    auth: 'Authentication',
    authHelp: 'ordered from most to least recommended',
    discouraged: 'discouraged: long-lived keys',
    badge: {
      recommended: 'Recommended',
      'no-secrets': 'No secrets',
      development: 'Development',
      discouraged: 'Discouraged',
    } as Record<string, string>,
    methods: {
      azureManagedIdentity: {
        label: 'Managed identity',
        description: 'A user-assigned or system-assigned identity of the host.',
      },
      azureFederated: {
        label: 'Workload identity federation',
        description: 'Client ID and a token file on the server.',
      },
      azureAmbient: {
        label: 'Host default chain (ambient)',
        description:
          'Uses whatever the server signs in with (environment, CLI, managed identity). For development.',
      },
      azureClientSecret: {
        label: 'Client secret',
        description: 'A long-lived key, referenced by its secret name in a store, never typed here.',
      },
      awsAssumeRole: {
        label: 'Assume role',
        description: 'Works across accounts; session names show in CloudTrail.',
      },
      awsFederated: {
        label: 'Federated (web identity)',
        description: 'A role and an OIDC token file on the server.',
      },
      awsAmbient: {
        label: 'Host default chain',
        description: 'Only when the server runs in the target account.',
      },
      awsAccessKey: {
        label: 'Access key',
        description: 'A long-lived key, referenced by its secret names in a store, never typed here.',
      },
      gcpImpersonate: {
        label: 'Impersonate a service account',
        description: 'The host’s credentials act as a service account.',
      },
      gcpFederated: {
        label: 'Workload identity federation',
        description: 'An external account configuration; no keys.',
      },
      gcpAmbient: {
        label: 'Host default credentials',
        description: 'Application Default Credentials of the server.',
      },
      gcpServiceAccountKey: {
        label: 'Service account key',
        description: 'A long-lived key, referenced by its secret name in a store, never typed here.',
      },
    },
    shortSubscription: (last: string) => `sub …${last}`,
    secretFieldHelp: 'This is the name of a secret in the store, never the value.',
    secretStore: 'Secret store',
    verify: 'Verify now',
    verifying: 'Verifying…',
    identity: 'Identity',
    signedInAs: 'Signed in as',
    checks: 'Checks',
    neverVerified: 'Never verified. Verify to check the credentials, scope and permissions.',
    hints: {
      credentials: 'Check the secret names and the secret store.',
      subscription: 'Check the subscription ID and that the identity has at least Reader on it.',
      'resource-providers':
        'Register them under Subscriptions → Resource providers, or with az provider register.',
      identity: 'Check the role ARN, its trust policy and the account ID.',
      project: 'Check the project ID and that the identity can read the project.',
      'service-usage': 'Enable the APIs in the console, or with gcloud services enable.',
      permissions: 'Grant the missing permissions to the identity, or activate an eligible role.',
    } as Record<string, string>,
    more: (name: string) => `More actions for ${name}`,
    delete: 'Delete cloud account',
    deleteShort: 'Delete',
    deleteConfirm: (name: string) => `Delete the cloud account “${name}”?`,
    inUse: 'Deployments still use this cloud account. Change their settings first.',
    inUseList: 'Delete or change these deployments first.',
    providerFixed: 'The provider can’t change after creation.',
    jsonInvalid: 'Enter valid JSON.',
    editTitle: (name: string) => `Cloud account ${name}`,
    meta: (provider: string, user: string, date: string, revision: number) =>
      `${provider} · created by ${user} on ${date} · revision r${revision}`,
    tabs: { settings: 'Settings', usedBy: 'Used by' },
    tabsLabel: 'Cloud account sections',
    createTitle: 'New cloud account',
    createAndVerify: 'Create and verify',
    viewOnly: 'Only admins can change cloud accounts.',
    saveBar: 'Admins edit and verify. Operators can view.',
    discard: 'Discard',
    saveChanges: 'Save changes',
    saved: 'Cloud account saved.',
    errorSummary: (n: number) => (n === 1 ? 'Fix 1 problem:' : `Fix ${n} problems:`),
    usedBy: 'Used by',
    usedByCount: (n: number) => (n === 1 ? '1 deployment' : `${n} deployments`),
    usedByNone: 'No deployment uses this account.',
    usedByExplain:
      'Either usage blocks deletion: a deployment’s settings name it, or its last apply used it (destroy and refresh need it).',
    usage: { settings: 'settings', 'last-apply': 'last apply' } as Record<string, string>,
    lastApplyOnly: 'last apply only',
    columns: { deployment: 'Deployment', blueprint: 'Blueprint', status: 'Status', usage: 'Usage' },
    groupLabel: (provider: string) => `${provider} accounts`,
    noOperator: 'You need the operator role to see cloud accounts.',
  },
  cloudAccountFields: {
    accountId: 'Account ID',
    tenantId: 'Tenant ID',
    subscriptionId: 'Subscription ID',
    projectId: 'Project ID',
    roleArn: 'Role ARN',
    externalId: 'External ID',
    sessionDurationMinutes: 'Session duration (minutes)',
    tokenFile: 'Token file',
    accessKeyIdSecret: 'Access key ID secret',
    secretAccessKeySecret: 'Secret access key secret',
    secretStore: 'Secret store',
    clientId: 'Client ID',
    clientSecretSecret: 'Client secret secret',
    serviceAccountEmail: 'Service account email',
    credentialConfig: 'Credential configuration',
    keySecret: 'Key secret',
  } as Record<string, string>,
  cloudAccountHelp: {
    accountId: '12 digits.',
    guid: 'A GUID.',
    tokenFile: 'Absolute path of the OIDC token file on the server.',
    sessionDurationMinutes: '15 to 60.',
    managedIdentityClientId: 'Optional: a user-assigned identity.',
    credentialConfig: 'External account JSON.',
  },
  dock: {
    zone: { left: 'Left dock', right: 'Right dock', bottom: 'Bottom dock' },
    zoneTabs: (zone: string) => `${zone} windows`,
    resize: (zone: string) => `Resize ${zone.toLowerCase()}`,
    collapsed: 'Collapsed',
    options: (title: string) => `Window options for ${title}`,
    dockLeft: (title: string) => `Dock ${title} left`,
    dockRight: (title: string) => `Dock ${title} right`,
    dockBottom: (title: string) => `Dock ${title} bottom`,
    float: (title: string) => `Float ${title}`,
    maximize: (title: string) => `Maximize ${title}`,
    restore: (title: string) => `Restore ${title}`,
    hide: (title: string) => `Hide ${title}`,
    show: (title: string) => `Show ${title}`,
    menu: {
      dockLeft: 'Dock left',
      dockRight: 'Dock right',
      dockBottom: 'Dock bottom',
      float: 'Float',
      maximize: 'Maximize',
      restore: 'Restore',
      hide: 'Hide',
      reset: 'Reset this window',
    },
    floatingSuffix: ' (floating)',
    maximizedSuffix: ' (maximized)',
    errors: (n: number) => `${n} ${n === 1 ? 'error' : 'errors'}`,
    warnings: (n: number) => `${n} ${n === 1 ? 'warning' : 'warnings'}`,
    runActive: 'run active',
    buttonLabel: (title: string, details: string[]) => [title, ...details].join(', '),
    layout: 'Layout',
    layoutMenu: 'Layout',
    resetLayout: 'Reset layout',
    dockAll: 'Dock all tool windows',
    hideAll: 'Hide all tool windows',
    dragHandle: (title: string) => `Move ${title}`,
    announce: {
      docked: (title: string, zone: 'left' | 'right' | 'bottom') => `${title} docked ${zone}`,
      floating: (title: string) => `${title} floating`,
      maximized: (title: string) => `${title} maximized`,
      restored: (title: string) => `${title} restored`,
      shown: (title: string) => `${title} shown`,
      hidden: (title: string, keys: string | null) =>
        keys ? `${title} hidden. Press ${keys} to show it.` : `${title} hidden.`,
      reset: 'Layout reset',
      dockedAll: 'All tool windows docked',
      hiddenAll: 'All tool windows hidden',
      collapsed: (zone: string) => `${zone} collapsed`,
      expanded: (zone: string) => `${zone} expanded`,
      narrow: 'Needs a window at least 1024 pixels wide.',
    },
    spokenKeys: (digit: number) => `Control Shift ${digit}`,
    windows: {
      problems: 'Problems',
      activity: 'Activity',
    },
    unavailable: {
      problems: 'Open a blueprint to see problems.',
      activity: 'Open a deployment, a run or a designer overlay to see activity.',
    },
    shortcuts: {
      group: 'Tool windows',
      toggle: (title: string) => `Show, focus or hide ${title}`,
      hide: 'Hide the focused tool window',
      maximize: 'Maximize or restore the focused tool window',
      dock: 'Dock the focused tool window left, right or bottom, or float it',
    },
    palette: {
      show: (title: string) => `Show ${title}`,
      dockLeft: (title: string) => `Dock ${title} left`,
      dockRight: (title: string) => `Dock ${title} right`,
      dockBottom: (title: string) => `Dock ${title} bottom`,
      float: (title: string) => `Float ${title}`,
      resetLayout: 'Reset layout',
      dockAll: 'Dock all tool windows',
      hideAll: 'Hide all tool windows',
    },
  },
  problems: {
    title: 'Problems',
    filter: 'Severity',
    all: 'All',
    errors: 'Errors',
    warnings: 'Warnings',
    search: 'Filter problems',
    severity: 'Severity',
    code: 'Code',
    message: 'Message',
    where: 'Where',
    none: 'No problems.',
    noMatch: 'No problems match the filter.',
    blueprint: 'blueprint',
    badgeValid: 'Valid',
    badge: (errors: number, warnings: number) =>
      [
        errors > 0 ? `${errors} ${errors === 1 ? 'error' : 'errors'}` : null,
        warnings > 0 ? `${warnings} ${warnings === 1 ? 'warning' : 'warnings'}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    badgeLabel: (text: string) => `Validation: ${text}. Show problems`,
  },
  activity: {
    title: 'Activity',
    run: (kind: string) => `${kind} run`,
    openRun: 'Open run',
    noActiveRun: (deployment: string) => `No run is active on ${deployment}.`,
    openDeployment: 'Open the deployment',
    openActivityTab: 'Open the Activity tab',
    tabs: 'Deployment',
    overview: 'Overview',
    listLabel: (deployment: string) => `Activity of ${deployment}`,
    showTarget: (target: string) => `Show ${target} in the Variables window`,
    loadMore: 'Load more',
    noMore: 'No more activity.',
    empty: 'No activity yet.',
    noMatch: 'No activity matches the filters.',
    clearFilters: 'Clear filters',
    filters: {
      label: 'Activity filters',
      kind: 'Kind',
      allKinds: 'All kinds',
      groups: { settings: 'Settings', runs: 'Runs', secrets: 'Secrets', apps: 'Apps', network: 'Network' },
      allOf: (group: string) => `All ${group.toLowerCase()} changes`,
      secret: 'Secret',
      secretPlaceholder: 'Secret name',
      resource: 'Resource',
      allResources: 'All resources',
      person: 'Person',
      personPlaceholder: 'Name or ID',
      onlyMine: 'Only mine',
    },
    kinds: {
      'deployment-created': 'Deployment created',
      'settings-changed': 'Settings changed',
      'deployment-deleted': 'Deployment deleted',
      'run-created': 'Run started',
      'run-completed': 'Run finished',
      'secret-set': 'Value set',
      'secret-generated': 'Value generated',
      'secret-accepted': 'Version accepted',
      'secret-ignored': 'Secret ignored',
      'tags-rewritten': 'Tags rewritten',
      'secret-recovered': 'Secret recovered',
      'references-refreshed': 'References refreshed',
      'app-restarted': 'App restarted',
      'secret-changed-outside': 'Version found outside Nebisys',
      'secret-revealed': 'Value revealed or copied',
      'secret-rotated': 'Rotated in the vault',
      'vault-secrets-deleted': 'Vault secrets deleted',
      'vault-secrets-restored': 'Vault secrets restored',
      ...networkRuns.activity.kinds,
      ...networkRequests.activityKinds,
      ...networkEntryPoints.activityKinds,
    } as Record<string, string>,
    actor: { system: 'Nebisys', rollback: 'Nebisys (rollback)' },
    outcome: { failed: 'Failed', canceled: 'Canceled', pending: 'Pending', succeeded: 'Succeeded' },
    sentence: {
      deploymentCreated: 'created the deployment',
      settingsChanged: 'changed the settings',
      deploymentDeleted: 'deleted the deployment',
      runCreated: (kind: string) => `started ${/^[aeiou]/.test(kind) ? 'an' : 'a'} ${kind} run`,
      vaultOnly: 'vault only',
      ofVersion: (version: number) => `of v${version}`,
      rollingBack: 'rolling back',
      runCompleted: (kind: string) => `${kind} run completed`,
      runFailed: (kind: string) => `${kind} run failed`,
      runCanceled: (kind: string) => `${kind} run was canceled`,
      startedBy: (user: string) => `started by ${user}`,
      secretSet: 'set a new value of',
      restarting: 'restarting its apps',
      secretGenerated: 'generated the value of',
      secretAccepted: 'accepted the version written outside Nebisys of',
      secretIgnored: 'ignored the unmanaged secret',
      tagsRewritten: 'rewrote Nebisys’s tags',
      tagsOn: 'on',
      secretRecovered: 'recovered',
      recoveryPending: 'asked to recover',
      stillRecovering: (seconds: number) => `still recovering after ${seconds} s`,
      referencesRefreshed: 'refreshed the vault references of',
      afterApply: 'after the apply',
      appRestarted: 'restarted',
      rolledOut: 'rolled out new tasks of',
      newRevision: 'deployed a new revision of',
      afterSetting: 'after setting',
      secretRotated: 'found a version rotated in the vault of',
      vaultSecretsDeleted: (n: number) => `deleted ${n} ${n === 1 ? 'secret' : 'secrets'} of the vault`,
      recoverableUntil: (until: string) => `· recoverable until ${until}`,
      permanently: '· permanently',
      kept: (names: string) => `· kept ${names}`,
      vaultSecretsRestored: (n: number) => `restored ${n} ${n === 1 ? 'secret' : 'secrets'} of the vault`,
      changedOutside: 'found a new version written outside Nebisys of',
      version: (id: string) => `version ${id}`,
      revealed: 'revealed the value of',
      copied: 'copied the value of',
      revealFailed: 'tried to reveal the value of',
      copyFailed: 'tried to copy the value of',
      revealCanceled: 'stopped revealing the value of',
      revealRequested: 'requested the value of',
      outcomeUnknown: '(outcome unknown)',
      purpose: (purpose: string) => `(${purpose})`,
      reason: (reason: string) => `· reason: ${reason}`,
      entry: 'activity entry',
    },
    paths: {
      parameter: (name: string) => `parameter ${name}`,
      name: 'name',
    },
    moreChanges: (n: number) => `+${n} more`,
    started: (time: string) => `started ${time}`,
    activeRuns: 'Active runs',
    nothingRunning: 'Nothing is running.',
    allRuns: 'All runs',
  },
  /** AWS and GCP managed vaults (feature spec 15, spec 6.10.19, 6.4, 6.13). */
  vaults: {
    aws: {
      label: 'AWS Secrets Manager',
      shortLabel: 'Secrets Manager',
      limitLabel: 'Secrets Manager limit',
      rotated: 'Rotated by Secrets Manager',
      rotatedSystem: 'Rotated by Secrets Manager · the next apply overwrites it',
      rotatedHelp: 'Secrets Manager rotated this secret: its newest version wasn’t written by Nebisys.',
      rotationConflict: 'Rotation conflicts',
      rotationConflictHelp: (secret: string) =>
        `Secrets Manager rotates ${secret} while Nebisys owns it: the next apply writes Nebisys’s password back while the database may keep the rotated one. Turn off rotation for this secret.`,
      rotationOn: (next: string | null) => (next ? `On · next ${next}` : 'On'),
      rotationOff: 'Off',
      recovery: (ago: string, until: string) => `Deleted ${ago} · recoverable until ${until}`,
      recoveryLabel: 'Recover within 30 days (scheduled deletion)',
      placement: (region: string) =>
        `Secrets go to AWS Secrets Manager in ${region}, under a name prefix of this deployment.`,
      placementFixed: (deployment: string, region: string, prefix: string) =>
        `Secrets of ${deployment} stay in AWS Secrets Manager in ${region} under ${prefix}. Moving them to another region needs a new deployment: values aren’t copied.`,
      prepare: (deployment: string, prefix: string, region: string) =>
        `${deployment}’s secrets aren’t set up in AWS Secrets Manager yet. Prepare the vault to set values before the first apply: Nebisys checks access and records ${prefix} in ${region}; nothing is created in AWS.`,
      deletedVault: (until: string) => `Scheduled for deletion · recoverable until ${until}`,
      deletedVaultText: (deployment: string, region: string) =>
        `Applying ${deployment} again in ${region} restores its secrets; Secrets Manager then deletes them.`,
      mechanism: 'Nebisys schedules them for deletion',
      caveat: 'Database passwords are deleted with their databases.',
      reveal: {
        before: 'Nebisys records the reveal with your name, then reads the current value of',
        from: 'from AWS Secrets Manager',
        after: 'CloudTrail logs the read with the activity entry’s ID in its user agent.',
      },
      caseNote: 'Secrets Manager names are case-sensitive; Nebisys stores declared names in lowercase.',
    },
    gcp: {
      label: 'GCP Secret Manager',
      shortLabel: 'Secret Manager',
      limitLabel: 'Secret Manager limit',
      expiredLabel: (day: string) => `Deletes ${day}`,
      expired: (secret: string, when: string) =>
        `Secret Manager deletes ${secret} and all its versions on ${when}, and it can’t be recovered. Remove the expiry in Secret Manager to keep it.`,
      expiredGeneric:
        'Secret Manager deletes this secret and all its versions when it expires, and it can’t be recovered. Remove the expiry in Secret Manager to keep it.',
      disabled:
        'The newest version is disabled: new instances of apps can’t read it, and new revisions fail. Set a value to add an enabled version, or enable it in Secret Manager.',
      rotation: (next: string | null) => (next ? `Notifications · next ${next}` : 'Notifications'),
      rotationHelp:
        'Secret Manager only sends a notification; a version your subscriber adds shows as Changed outside.',
      recoveryMarked: (until: string) => `Deleted with the vault · recoverable until ${until}`,
      recoveryGone: 'Deleted in Secret Manager · can’t be recovered',
      recoveryLabel: 'None for outside deletions; Nebisys keeps destroyed vaults 30 days',
      placement: (region: string) =>
        `Secrets go to GCP Secret Manager in the project of the deployment’s GCP cloud account, replicated only to ${region}.`,
      placementFixed: (deployment: string, region: string) =>
        `Secrets of ${deployment} stay in GCP Secret Manager, replicated only to ${region}; Secret Manager can’t change that after a secret exists. Moving them needs a new deployment: values aren’t copied.`,
      prepare: (deployment: string, project: string) =>
        `${deployment}’s secrets aren’t set up in GCP Secret Manager yet. Prepare the vault to set values before the first apply: it turns on the Secret Manager API in ${project}.`,
      deletedVault: (until: string) => `Deleted · recoverable until ${until}`,
      deletedVaultText: (deployment: string, region: string) =>
        `Applying ${deployment} again in ${region} restores its secrets; Secret Manager then deletes them permanently. Their disabled versions are billed until then.`,
      mechanism: 'Nebisys disables their versions and sets them to expire then',
      caveat: 'Their disabled versions are billed until then.',
      reveal: {
        before: 'Nebisys records the reveal with your name, then reads the current value of',
        from: 'from GCP Secret Manager',
        after: 'Cloud Audit Logs record the read only when Data Access logs are on for Secret Manager.',
      },
      disabledReveal: 'Secret Manager refuses to read a disabled version.',
      caseNote: 'Secret Manager IDs are case-sensitive; Nebisys stores declared names in lowercase.',
      labels: 'Labels',
    },
    ecs: {
      reference: 'Secrets Manager reference · ECS Express',
      plain: 'Environment variable · ECS Express',
      pickup: (app: string) =>
        `Running tasks of ${app} keep the value they started with; new tasks get the new value.`,
      pickupPlain: (app: string) => `${app} gets the value at the next apply.`,
      nowAction: (apps: string) => `Roll out new tasks of ${apps}`,
      nowShort: 'Roll out new tasks',
      disruption:
        'ECS starts new tasks next to the old ones and moves traffic over in about 6 minutes; no downtime.',
      defaultReason: (secret: string) =>
        `Checked although ${secret}’s On change is Wait: ECS tasks never pick up a new value by themselves.`,
      done: (apps: string) => `Rolling out new tasks of ${apps}: about 6 minutes, no downtime.`,
      failed: (app: string, error: string) =>
        `Value set. Rolling out new tasks of ${app} failed: ${error}. Running tasks keep the old value; new tasks get the new one.`,
      pending: 'Rolling out',
      resolved: 'Running',
      resolvedHelp: (app: string, checked: string, read: string) =>
        `${app}’s tasks started with their secrets (checked ${checked}; oldest task ${read}).`,
      raw: { TasksFailing: 'Tasks failing' } as Record<string, string>,
      failure: (app: string) =>
        `New tasks of ${app} can’t start: ECS couldn’t read the secret. Running tasks keep their values.`,
      grantDelay: (app: string) =>
        `IAM changes can take a few minutes; ECS keeps starting new tasks of ${app} until they work.`,
      older: 'Uses an older version',
      olderHelp: (app: string, read: string) =>
        `At least one task of ${app} started before this value changed (oldest: ${read}) and still has the previous value. New tasks get the new one.`,
      rollingOut: (ago: string, by: string) => `Rolling out · started ${ago} by ${by} · about 6 minutes`,
      settingsChange: (app: string) => `Changing app settings rolls out new tasks of ${app}.`,
      refreshFallback: 'Use Roll out new tasks instead.',
      activity: (app: string) => `rolled out new tasks of ${app}`,
    },
    cloudRun: {
      reference: 'Secret Manager reference · Cloud Run',
      plain: 'Environment variable · Cloud Run',
      pickup: (app: string) =>
        `Running instances of ${app} keep the value they started with; new instances get the new value.`,
      pickupPlain: (app: string) => `${app} gets the value at the next apply.`,
      nowAction: (apps: string) => `Deploy a new revision of ${apps}`,
      nowShort: 'Deploy a new revision',
      disruption: 'Cloud Run starts the new revision and moves traffic to it once it’s ready; no downtime.',
      defaultReason: (secret: string) =>
        `Checked although ${secret}’s On change is Wait: Cloud Run instances never pick up a new value by themselves.`,
      done: (apps: string) => `Deploying a new revision of ${apps}.`,
      failed: (app: string, error: string) =>
        `Value set. Deploying a new revision of ${app} failed: ${error}. Running instances keep the old value; new instances get the new one.`,
      pending: 'Deploying revision',
      resolved: 'Ready',
      resolvedHelp: (app: string, checked: string, read: string) =>
        `${app}’s serving revision started with its secrets (checked ${checked}; revision created ${read}).`,
      raw: { RevisionFailed: 'Revision failed' } as Record<string, string>,
      failure: (app: string) =>
        `New instances of ${app} can’t start and new revisions fail to deploy. Running instances keep their values.`,
      grantDelay: (app: string) =>
        `IAM changes can take a few minutes. If ${app}’s revision failed, deploy a new revision then.`,
      older: 'May use an older version',
      olderHelp: (app: string, time: string) =>
        `${app}’s serving revision started before this value changed: instances started before ${time} have the previous value, newer ones the new value.`,
      rollingOut: (ago: string, by: string) => `Deploying revision · started ${ago} by ${by}`,
      settingsChange: (app: string) => `Changing app settings deploys a new revision of ${app}.`,
      refreshFallback: 'Use Deploy a new revision instead.',
      activity: (app: string) => `deployed a new revision of ${app}`,
    },
    vaultNoun: 'Prefix',
    secretsUnder: (prefix: string) => `Secrets under ${prefix}`,
    secretId: 'Secret ID',
    versionsKept: (kept: number, days: number) =>
      `Versions kept: ${kept} · older ones are disabled at once and destroyed after ${days} days`,
    nameConflict: 'Name conflict',
    nameConflictHelp: (native: string, label: string, name: string) =>
      `${native} also exists in ${label} with different case. Nebisys uses ${name}; delete or rename the other one.`,
    foreign: 'another deployment',
    foreignHelp: (deployment: string, prefix: string) =>
      `Belongs to deployment ${deployment} (tag nebisys-deployment). Two deployments share the prefix ${prefix}.`,
    // The destroy template (6.4).
    deletionWhat: (deployment: string, label: string, prefix: string) =>
      `${deployment}’s secrets in ${label} (${prefix}…)`,
    deletion: (
      what: string,
      until: string,
      mechanism: string,
      deployment: string,
      region: string,
      store: string,
      caveat: string,
    ) =>
      `${what} stay recoverable until ${until}: ${mechanism}, and applying ${deployment} again in ${region} restores them. After that ${store} deletes them permanently. ${caveat}`,
    deletionNow: (deployment: string, label: string) =>
      `${deployment}’s secrets in ${label} are deleted permanently at once: this installation keeps no recovery window.`,
    otherSecrets: (n: number, prefix: string) =>
      `${n} other secrets under ${prefix} aren’t Nebisys’s and stay.`,
    failedDeletion: (deployment: string, n: number | null, label: string, prefix: string) =>
      `The destroy couldn’t delete ${deployment}’s secrets: ${n === null ? 'its' : n} secrets stay in ${label} under ${prefix} and are billed. Destroy ${deployment} again to delete them.`,
    secretsRemain: (deployment: string, label: string) =>
      `Destroy ${deployment} again to delete its secrets in ${label} first, or delete ${deployment} and keep its secrets.`,
    deleteKeep: 'Delete and keep its secrets…',
    deleteKeepTitle: (deployment: string) => `Delete ${deployment} and keep its secrets`,
    deleteKeepText: (n: number | null, label: string, prefix: string, region: string) =>
      `${n === null ? 'Its' : n} secrets stay in ${label} under ${prefix} in ${region} and stay billed; the activity keeps their names.`,
    deleteKeepSubmit: 'Delete and keep secrets',
    typeName: (name: string) => `Type “${name}” to confirm.`,
    // Plan review and Apply (U2.1).
    planDeletion: (
      deployment: string,
      d: { count: number; label: string; prefix: string },
      until: string,
      system: boolean,
    ) =>
      `The apply deletes ${deployment}’s ${d.count} secrets in ${d.label} (${d.prefix}…): recoverable until ${until} by switching back to the managed vault.${system ? ' Database passwords are deleted and generated again.' : ''}`,
    planDeletionNow: (deployment: string, d: { count: number; label: string; prefix: string }) =>
      `The apply deletes ${deployment}’s ${d.count} secrets in ${d.label} (${d.prefix}…) permanently.`,
    // Vault mode (6.13 item 6, create step 4).
    modeLegend: 'Where secret values live',
    managedOption: (label: string, region: string) => `Managed vault: ${label} in ${region}`,
    managedFixed: 'Managed vault · fixed',
    managedHelp:
      'Apps get references, never values, and read them when they start. Values are not copied from the secret store: you set them in the vault.',
    storeOption: (store: string) => `Secret store ${store}`,
    chosenForYou: 'chosen for you',
    willBeDeleted: (deployment: string, label: string, days: number) =>
      days > 0
        ? `The next apply deletes ${deployment}’s secrets in ${label}; they stay recoverable for ${days} days, and switching back restores them. Nebisys doesn’t copy values into the store: make sure the store holds every value first.`
        : `The next apply deletes ${deployment}’s secrets in ${label} permanently: this installation keeps no recovery window. Nebisys doesn’t copy values into the store: make sure the store holds every value first.`,
    saveToManaged: (label: string) => `secrets move to ${label} · next: prepare the vault, set values, plan`,
    saveToStore: (store: string) => `secrets come from the store ${store} at the next apply`,
    saveToStoreDeleting: (store: string, label: string, days: number) =>
      days > 0
        ? `secrets come from the store ${store} at the next apply; that apply deletes the secrets in ${label} (recoverable for ${days} days); database passwords are generated again`
        : `secrets come from the store ${store} at the next apply; that apply deletes the secrets in ${label} permanently; database passwords are generated again`,
    // Switch in progress (19.7).
    toManaged: (deployment: string, label: string, store: string) =>
      `${deployment} is moving its secrets to ${label}. Until the next apply the apps keep the values copied from the store ${store}.`,
    toManagedSteps: {
      prepare: 'Prepare the vault',
      values: (set: number, total: number) => `Set values: ${set} of ${total}`,
      apply: 'Plan and apply: the apps move to references',
    },
    useStoreInstead: 'Use the secret store instead',
    toStore: (deployment: string, store: string, n: number, label: string, until: string | null) =>
      `${deployment} is moving its secrets to the secret store ${store}. The next apply copies the store’s values into the apps as plain settings and deletes the ${n} secrets in ${label} (${until ? `recoverable until ${until}; switching back restores them` : 'permanently'}). Nebisys doesn’t copy values into the store: make sure ${store} holds every value first.`,
    useManagedInstead: 'Use the managed vault instead',
    plan: 'Plan',
    // Per-app status, rollouts (19.3).
    appStrip: (app: string) => `Reference status of ${app}`,
    checked: (ago: string) => `checked ${ago}`,
    oldestTask: (ago: string) => `oldest task started ${ago}`,
    revisionCreated: (ago: string) => `revision created ${ago}`,
    appDetails: (app: string) => `Details of ${app}`,
    rolloutBusy: 'A run is active.',
    rollingOutSince: (time: string) => `Rolling out since ${time}`,
    stillRollingOut: 'Still rolling out after 10 minutes · Refresh to check',
    rolledOut: (app: string) => `${app} rolled out: its tasks use the current values.`,
    rolloutFailed: (app: string, message: string) => `${app}’s rollout failed: ${message}`,
    confirmRestart: (app: string) => `Restart ${app}`,
    restart: 'Restart',
    restartDisruption: 'It restarts all its instances; requests can fail briefly.',
    rolloutDisabledRun:
      'A run is active, so new tasks aren’t rolled out now. The running apply rolls out apps whose settings change; roll out the others when it ends.',
    recreated: (secret: string, app: string, deployment: string) =>
      `Setting a value creates ${secret} again with a new ID. ${app} keeps its running tasks; new tasks can’t read it until the next apply. Plan and apply ${deployment} after setting it.`,
    usedByOlder: (app: string) => `${app} (older version)`,
    // App details, badges and menus (19.3), rotation column (19.5), native names (19.6).
    appDetailsDelivery: 'Delivery',
    appDetailsStatus: 'Reference status',
    appDetailsRawStatus: (status: string) => `Platform status: ${status}`,
    appDetailsReadAt: 'Read references',
    appDetailsReadAtValue: (at: string, ago: string) => `${at} (${ago})`,
    appDetailsOlder: 'Rows that use an older version',
    appDetailsNoOlder: 'No row uses an older version.',
    appDetailsRollout: 'Rollout',
    appDetailsNoStatus: 'The platform hasn’t reported a status yet.',
    groupRowMenu: (app: string) => `Actions for ${app}`,
    groupRowName: (app: string, type: string, count: string, issues: string, status: string | null) =>
      [app, type, count, issues, status].filter(Boolean).join(', '),
    appStatusOf: (app: string) => `Reference status of ${app} (all its secrets):`,
    appStatusBadge: (app: string, status: string) => `${app}: ${status}`,
    appNeutral: {
      'access-denied': 'Access denied',
      'not-found': 'Secret not found',
      invalid: 'Invalid reference',
      unreachable: 'Couldn’t read the secret',
    } as Record<string, string>,
    rotationColumn: 'Rotation',
    rotationNone: '–',
  },
  about: {
    title: 'About Nebisys',
    text: 'Nebisys designs cloud architectures once and deploys them to AWS, Azure, Google Cloud or a mix.',
    serverVersion: 'Version',
    build: 'Build',
    commit: 'Commit',
    noCommit: 'Not recorded in this build',
    webApp: 'Web app',
    webAppDiffers: (web: string, server: string) =>
      `This page was loaded for Nebisys ${web}, but the server runs ${server}. Reload the page to get the current web app.`,
    whatsNew: 'What’s new in Nebisys',
    user: 'User',
    roles: 'Roles',
    authMode: 'Authentication',
  },
  // What’s new (spec 11, 6.14).
  whatsNew: {
    title: 'What’s new',
    intro: (version: string) =>
      `The changes in each version of Nebisys, newest first. This installation runs version ${version}.`,
    versions: 'Versions',
    jumpTo: 'Jump to',
    unreleased: 'Not released yet',
    unreleasedHelp: 'Changes made since the last release. They are part of this build.',
    release: (version: string) => `Version ${version}`,
    current: 'Running now',
    breaking: 'Breaking',
    empty: 'The changelog has no releases yet.',
    category: {
      added: 'Added',
      changed: 'Changed',
      deprecated: 'Deprecated',
      removed: 'Removed',
      fixed: 'Fixed',
      security: 'Security',
    } satisfies Record<ChangelogCategory, string>,
  },
  status: {
    run: {
      pending: 'Pending',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
      canceled: 'Canceled',
    } satisfies Record<RunStatus, string>,
    deployment: {
      new: 'New',
      active: 'Active',
      failed: 'Failed',
      destroyed: 'Destroyed',
    } satisfies Record<DeploymentStatus, string>,
    drift: {
      unknown: 'Unknown',
      none: 'No drift',
      detected: 'Drift detected',
    } satisfies Record<DriftState, string>,
    resource: {
      pending: 'Pending',
      'in-progress': 'In progress',
      completed: 'Completed',
      failed: 'Failed',
      skipped: 'Skipped',
    } satisfies Record<ResourceRunStatus, string>,
    action: {
      create: 'Create',
      update: 'Update',
      replace: 'Replace',
      delete: 'Delete',
      'no-change': 'No change',
    } satisfies Record<ChangeAction, string>,
    level: {
      debug: 'Debug',
      info: 'Info',
      warning: 'Warning',
      error: 'Error',
    } satisfies Record<EventLevel, string>,
    runKind: {
      plan: 'Plan',
      apply: 'Apply',
      destroy: 'Destroy',
      refresh: 'Refresh',
    } satisfies Record<RunKind, string>,
    severity: { error: 'Error', warning: 'Warning', info: 'Info' },
  },
};
